import { useState, useEffect } from "react";
import Navbar from "../Navbar/Navbar";

// ─── DESIGN TOKENS (matches PPCACrashCourse.jsx) ──────────────────
const C = {
  navy:    "#0B1F3A",
  navy2:   "#132d52",
  gold:    "#C9A84C",
  goldL:   "#FDF6E3",
  white:   "#FFFFFF",
  offwhite:"#F8FAFC",
  slate:   "#F1F5F9",
  border:  "#E2E8F0",
  muted:   "#64748B",
  dark:    "#1E293B",
  green:   "#059669",
  greenL:  "#ECFDF5",
  blue:    "#1D4ED8",
  blueL:   "#EFF6FF",
  amber:   "#B45309",
  amberL:  "#FFFBEB",
  red:     "#DC2626",
  redL:    "#FEF2F2",
  purple:  "#7C3AED",
  purpleL: "#F5F3FF",
};

// ─── THEME HELPER — every themed surface pulls from here so light
// and dark mode stay consistent across the whole app ───────────────
function getTheme(dark) {
  return {
    dark,
    pageBg:      dark ? "#0d1f3c" : C.offwhite,
    heroGrad:    "linear-gradient(135deg,#0d1f3c 0%,#142a4d 100%)",
    cardBg:      dark ? "#142a4d" : C.white,
    cardBg2:     dark ? "#0f2748" : C.slate,
    cardBorder:  dark ? "rgba(255,255,255,0.12)" : C.border,
    heading:     dark ? "#ffffff" : C.navy,
    text:        dark ? "#eef2f8" : C.dark,
    subtext:     dark ? "rgba(238,242,248,0.62)" : C.muted,
    inputBg:     dark ? "#0f2748" : C.offwhite,
    overlay:     "rgba(4,10,22,0.72)",
    shadow:      dark ? "0 10px 30px rgba(0,0,0,.45)" : "0 10px 30px rgba(15,35,70,.10)",
  };
}

const STATE_FLAGS = {
  AL:"🌲", AR:"🌾", AZ:"🌵", CA:"🌉", CO:"🏔️", CT:"⚓", DE:"💎",
  FL:"🌴", GA:"🍑", HI:"🌺", ID:"🥔", IL:"🌽", IN:"🏎️", IA:"🌽",
  KS:"🌾", KY:"🏇", LA:"🎷", ME:"🦞", MD:"🦀", MA:"🦃", MI:"🚗",
  MN:"❄️", MS:"🎵", MO:"🏛️", MT:"🦌", NE:"🌾", NV:"🎰", NH:"🍁",
  NJ:"🗽", NM:"🌶️", NY:"🗽", NC:"⭐", ND:"🌻", OH:"✈️", OK:"🛢️",
  OR:"🌲", PA:"🔔", RI:"⚓", SC:"🌴", SD:"🏔️", TN:"🎵", TX:"⭐",
  UT:"⛷️", VT:"🍁", VA:"🏛️", WA:"🌲", WV:"⛏️", WI:"🧀", WY:"🦬",
  DC:"🏛️",
};

// ─────────────────────────────────────────────────────────────────
// GROUP MAP — every state lives in exactly one of these 4 groups.
// pevo / eight / standalone carry full comparison data (width, height,
// insurance, night rule, cert type). "remaining" states are listed by
// name only until their state-specific data is added — the course
// still works for them using the federal BASE_MODULES.
// ─────────────────────────────────────────────────────────────────
const GROUPS = {
  pevo: {
    id: "pevo", label: "14 PEVO States", sublabel: "Performance-Based",
    color: C.green, colorL: C.greenL, icon: "🛡️",
    description: "Accept a national PEVO certificate as sufficient. No separate state permit is required — carry the original certificate and the load's permit.",
    keyRule: "PPCA/PEVO certificate accepted. No separate state permit required.",
    states: [
      { code:"AL", name:"Alabama",        width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"AR", name:"Arkansas",       width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"CO", name:"Colorado",       width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"CT", name:"Connecticut",    width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"PEVO Cert" },
      { code:"DE", name:"Delaware",       width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"FL", name:"Florida",        width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO + Def. Driving" },
      { code:"GA", name:"Georgia",        width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"KY", name:"Kentucky",       width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"LA", name:"Louisiana",      width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"MI", name:"Michigan",       width:"14'6\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"PEVO Cert" },
      { code:"MS", name:"Mississippi",    width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"NC", name:"North Carolina", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"NM", name:"New Mexico",     width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"SC", name:"South Carolina", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
    ],
  },
  eight: {
    id: "eight", label: "8-State Group", sublabel: "Uniform Requirements",
    color: C.blue, colorL: C.blueL, icon: "🔷",
    description: "Share one uniform equipment and dimension standard. Washington is the exception inside this group — it layers its own WSDOT Pilot Car Operator Permit on top.",
    keyRule: "Uniform 14'6\" / 14'6\" / 95'0\" baseline. Washington also requires its own state permit.",
    states: [
      { code:"AZ", name:"Arizona",    width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"ID", name:"Idaho",      width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"No cert" },
      { code:"IA", name:"Iowa",       width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"KS", name:"Kansas",     width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"MO", name:"Missouri",   width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"NE", name:"Nebraska",   width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"NV", name:"Nevada",     width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"UT", name:"Utah",       width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"WA", name:"Washington", width:"12'0\"", height:"14'0\"", length:"75'0\"", ins:"$500K CSL", night:"No Fri 3pm–Mon 6am", cert:"WSDOT Pilot Car Permit", alert:true },
    ],
  },
  standalone: {
    id: "standalone", label: "Stand-Alone States", sublabel: "Independent Requirements",
    color: C.amber, colorL: C.amberL, icon: "⭐",
    description: "Zero reciprocity. Each requires its own permit or certification obtained before crossing the border — no national cert substitutes.",
    keyRule: "Get the state-specific permit BEFORE crossing the border.",
    states: [
      { code:"CA", name:"California", width:"14'0\"", height:"14'0\"", length:"95'0\"",  ins:"$750K CSL", night:"No Fri 3pm–Mon 6am", cert:"CALTRANS Permit", alert:true },
      { code:"OR", name:"Oregon",     width:"14'6\"", height:"15'6\"", length:"95'0\"",  ins:"$750K CSL", night:"Restricted",         cert:"Permit-specific" },
      { code:"TN", name:"Tennessee",  width:"14'6\"", height:"15'0\"", length:"95'0\"",  ins:"$750K CSL", night:"w/ lighting",        cert:"No cert" },
      { code:"TX", name:"Texas",      width:"16'0\"", height:"16'0\"", length:"110'0\"", ins:"$750K CSL", night:"Allowed",            cert:"TxDOT License", alert:true },
      { code:"WI", name:"Wisconsin",  width:"14'6\"", height:"15'0\"", length:"95'0\"",  ins:"$750K CSL", night:"w/ lighting",        cert:"No cert" },
      { code:"WY", name:"Wyoming",    width:"14'6\"", height:"14'6\"", length:"95'0\"",  ins:"$750K CSL", night:"Allowed",            cert:"No cert" },
    ],
  },
  remaining: {
    id: "remaining", label: "Remaining States", sublabel: "To Complete All 50",
    color: C.muted, colorL: C.slate, icon: "🗺️",
    description: "Varied frameworks — some accept PEVO, some require their own permit. State-specific numbers haven't been loaded yet, so these run on the federal baseline course until added.",
    keyRule: "Verify current requirements with that state's DOT before every move.",
    states: [
      { code:"AK", name:"Alaska",          width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"DC", name:"Washington DC",   width:"13'0\"", height:"13'6\"", ins:"$1M CSL",   night:"Restricted",  cert:"No cert",        alert:true },
      { code:"HI", name:"Hawaii",          width:"10'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert",        alert:true },
      { code:"IL", name:"Illinois",        width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"IN", name:"Indiana",         width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"MD", name:"Maryland",        width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"No cert" },
      { code:"MA", name:"Massachusetts",   width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"No cert" },
      { code:"ME", name:"Maine",           width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"w/ lighting", cert:"No cert" },
      { code:"MN", name:"Minnesota",       width:"14'6\"", height:"15'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"MT", name:"Montana",         width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"ND", name:"North Dakota",    width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"OH", name:"Ohio",            width:"14'6\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"OK", name:"Oklahoma",        width:"14'6\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"PA", name:"Pennsylvania",    width:"14'6\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"No cert" },
      { code:"RI", name:"Rhode Island",    width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"No cert" },
      { code:"SD", name:"South Dakota",    width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"VA", name:"Virginia",        width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"No cert" },
      { code:"VT", name:"Vermont",         width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"No cert" },
      { code:"WV", name:"West Virginia",   width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"No cert" },
      { code:"NY", name:"New York",        width:"16'0\"", height:"15'0\"", ins:"$1M CSL",   night:"Restricted",  cert:"NYSDOT Required", alert:true },
      { code:"NJ", name:"New Jersey",      width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"No cert" },
      { code:"NH", name:"New Hampshire",   width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
    ],
  },
};

function findStateMeta(code) {
  for (const g of Object.values(GROUPS)) {
    const s = g.states.find((x) => x.code === code);
    if (s) return { ...s, group: g.id, groupColor: g.color, groupLabel: g.label };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────
// BASE_MODULES — the shared 14-module federal/MUTCD curriculum.
// Every state uses this by default. Nothing here is state-specific.
// ─────────────────────────────────────────────────────────────────
const BASE_MODULES = [
  {
    num: 1, icon: "🚔", time: "20 min",
    title: "The Pilot/Escort Vehicle Industry — Roles & Authority",
    objectives: [
      "Understand the role and legal responsibilities of a pilot/escort vehicle operator",
      "Know the difference between federal and state regulatory authority",
      "Understand what types of loads generally require an escort",
      "Know the general purpose of an oversize/overweight permit",
    ],
    blocks: [
      { type: "para", text: "The pilot/escort vehicle operator (PEVO) safely guides oversize and overweight (OS/OW) loads on public highways — wind turbine blades, manufactured homes, bridge beams, and industrial equipment that exceed standard legal dimensions. As an escort, you are the advance warning system and traffic manager for every permitted move." },
      { type: "para", text: "Federal law (23 USC 127 and 49 USC 31111–31114) sets maximum legal vehicle dimensions on the Interstate Highway System, and no state can exceed those limits there. Each state, however, holds its own authority over permitting and certification on state highways, county roads, and local streets." },
      { type: "key", title: "Key Point", text: "Every state requires an oversize/overweight permit before a load exceeding legal dimensions can move on public roads. Escort requirements are layered on top of the permit and vary significantly by state." },
      { type: "tip", title: "Exam Tip", text: "Confirm whether your state accepts a national PEVO certificate alone, or requires an additional state-specific permit — this single distinction defines which of the four state groups you fall into." },
    ],
    practice: [
      { q: "What federal law sets maximum vehicle dimensions on the Interstate Highway System?", options: ["The MUTCD", "23 USC 127 and 49 USC 31111–31114", "OSHA 1910", "NFPA 10"], correct: 1, explanation: "23 USC 127 and 49 USC 31111–31114 set the federal maximum vehicle dimensions on Interstates; states retain authority over their own highways." },
    ],
  },
  {
    num: 2, icon: "📏", time: "25 min",
    title: "Federal Size & Weight Regulations",
    objectives: [
      "Know the federal maximum dimensions for all measurement categories",
      "Understand the purpose of the Bridge Formula",
      "Know why permit trigger widths differ from escort trigger widths",
      "Understand how state limits build on federal limits",
    ],
    blocks: [
      { type: "para", text: "Federal law sets the legal baseline on U.S. highways without a permit: 8'6\" wide, 65 ft long single unit (75 ft twin-trailer), 80,000 lbs gross vehicle weight, 20,000 lbs single axle, 34,000 lbs tandem axle." },
      { type: "para", text: "The Bridge Formula (Formula B) limits how much weight can be carried across a given span of axles, protecting bridge and road infrastructure. States may add their own bridge-specific restrictions on top of the federal formula." },
      { type: "key", title: "Key Point", text: "Any load exceeding the federal legal maximums requires an oversize/overweight permit in every state along the route." },
      { type: "tip", title: "Exam Tip", text: "Learn your state's specific escort-trigger widths — they typically start above the federal 8'6\" baseline, often at 10, 12, or 14 feet, and are separate from the permit trigger." },
    ],
    practice: [
      { q: "What is the federal maximum vehicle width allowed without a permit?", options: ["8'0\"", "8'6\"", "10'0\"", "12'0\""], correct: 1, explanation: "Federal law sets 8'6\" (102 inches) as the maximum legal width without an oversize permit." },
    ],
  },
  {
    num: 3, icon: "🔧", time: "30 min",
    title: "Vehicle & Equipment Requirements",
    objectives: [
      "Know the FHWA/MUTCD equipment baseline required in every state",
      "Know OVERSIZE LOAD sign specifications",
      "Understand amber light and safety vest requirements",
      "Know when to check for state-specific equipment additions",
    ],
    blocks: [
      { type: "para", text: "Every escort vehicle nationwide follows the same federal FHWA/MUTCD equipment baseline: amber rotating or strobe lights visible at 500 ft minimum · an OVERSIZE LOAD sign at least 18\"×84\", yellow background with black retroreflective letters · a CB radio defaulting to Channel 19 · red or orange flags at least 18\"×18\" · an ANSI Class 2 vest inside the vehicle and Class 3 on the roadway · a 2.5 lb ABC fire extinguisher · an OSHA Class A first aid kit." },
      { type: "key", title: "Key Point", text: "This equipment list is the national floor. Several states add requirements on top of it — always check your state's supplement before a move." },
      { type: "tip", title: "Exam Tip", text: "Memorize the federal minimums cold: 18\"×84\" sign, 18\"×18\" flags, 500 ft light visibility, 2.5 lb ABC extinguisher. State-specific additions are always tested separately." },
    ],
    practice: [
      { q: "What is the minimum size for an OVERSIZE LOAD sign under federal standards?", options: ["12\" × 60\"", "18\" × 84\"", "24\" × 96\"", "10\" × 48\""], correct: 1, explanation: "The federal minimum OVERSIZE LOAD sign size is 18\" tall by 84\" wide, yellow background with black retroreflective letters." },
    ],
  },
  {
    num: 4, icon: "📄", time: "25 min",
    title: "Reading & Understanding Oversize Permits",
    objectives: [
      "Identify the key components of an oversize permit",
      "Understand the general purpose of a move window",
      "Know how to handle permit discrepancies before a move",
      "Understand that expiration and ferry/route rules vary by state",
    ],
    blocks: [
      { type: "para", text: "An oversize permit is a legal document that must be reviewed in full before departure. It specifies load information (dimensions, description, configuration), the authorized route, the move window, escort requirements, and any special conditions such as bridge restrictions or nighttime authorization." },
      { type: "key", title: "Key Point", text: "A permit's authorized route is legally binding — deviating from it, even for a shortcut, is a violation regardless of state." },
      { type: "warn", title: "Warning", text: "Permit expiration rules differ by state — some allow a grace period, others (like Washington) expire at a hard midnight deadline with none. Always confirm your state's rule before departure." },
      { type: "tip", title: "Exam Tip", text: "Read every line of the permit before a move, not just the dimensions — lane restrictions, timing windows, and special conditions are frequently tested." },
    ],
    practice: [
      { q: "Which part of an oversize permit is legally binding and cannot be deviated from without violation?", options: ["The load description only", "The authorized route", "The carrier's name", "The escort company's logo"], correct: 1, explanation: "The authorized route specified on the permit is legally binding for the entire move." },
    ],
  },
  {
    num: 5, icon: "🗺️", time: "25 min",
    title: "Route Planning & Surveys",
    objectives: [
      "Understand the purpose of a route survey",
      "Know what a standard survey checklist covers",
      "Understand why some routes need special coordination",
      "Know how to plan for state-specific route features",
    ],
    blocks: [
      { type: "para", text: "A route survey should be completed as close to move day as possible, since conditions shift with weather and construction. A thorough survey checks bridge clearances, overhead utilities, active construction, and traffic signal timing in urban areas." },
      { type: "key", title: "Key Point", text: "Some states have unique route features — mountain passes, floating bridges, or ferry crossings — that require additional coordination beyond a standard survey. These are always state-specific." },
      { type: "tip", title: "Exam Tip", text: "Build extra time into any route plan that includes a unique state feature (mountain pass, ferry, seasonal closure) — these are the most commonly tested route-planning topics." },
    ],
    practice: [
      { q: "What should a route survey specifically check regarding bridges?", options: ["Paint color", "Vertical clearance", "Number of lanes only", "Toll price"], correct: 1, explanation: "A route survey must verify vertical bridge clearances against the load's actual height." },
    ],
  },
  {
    num: 6, icon: "✅", time: "20 min",
    title: "Pre-Trip Inspection",
    objectives: [
      "Complete a thorough pre-trip inspection",
      "Verify load dimensions and permit compliance before departure",
      "Establish communication with the carrier driver",
      "Understand the legal importance of pre-trip documentation",
    ],
    blocks: [
      { type: "para", text: "Before every move, verify all equipment is functional and confirm load dimensions match the permit. Document the inspection — in the event of an enforcement stop or accident, regulators will expect evidence of pre-trip compliance." },
      { type: "warn", title: "Warning", text: "If load dimensions do not match the permit — even by one inch — do not proceed. Contact the permitting authority to amend the permit before the move begins." },
      { type: "tip", title: "Exam Tip", text: "Pre-trip questions typically test whether you know to verify dimensions against the permit before departure, not just check equipment." },
    ],
    practice: [
      { q: "What should you do if measured load dimensions do not match the permit?", options: ["Proceed carefully", "Round to the nearest foot and continue", "Do not proceed — contact the permitting authority", "Only stop if the difference is over 6 inches"], correct: 2, explanation: "Any mismatch between actual load dimensions and the permit means the move should not proceed until the permit is corrected." },
    ],
  },
  {
    num: 7, icon: "🚩", time: "30 min",
    title: "MUTCD Flagging Standards",
    objectives: [
      "Know MUTCD Chapter 6 flagging requirements",
      "Know all STOP/SLOW paddle specifications and hand signals",
      "Understand flagger positioning requirements",
      "Know when state supplements override federal MUTCD rules",
    ],
    blocks: [
      { type: "para", text: "The moment you control traffic flow during a permitted move, you become a flagger under MUTCD Chapter 6 and are legally responsible for the safety of every road user in your control zone." },
      { type: "para", text: "The STOP/SLOW paddle must be at least 18 inches in diameter. The STOP face is octagonal with a red retroreflective background and white letters; the SLOW face is rectangular with an orange background and black letters. Flaggers must remain visible from at least 500 feet." },
      { type: "key", title: "Key Point", text: "Many states adopt the MUTCD together with their own state supplement. Where a state supplement is more restrictive, it takes precedence over the federal MUTCD." },
      { type: "tip", title: "Exam Tip", text: "The STOP face is octagonal, matching a standard stop sign — this is the most commonly missed detail on flagging sections of state exams." },
    ],
    practice: [
      { q: "What is the minimum size for a STOP/SLOW flagging paddle under MUTCD standards?", options: ["12 inches", "16 inches", "18 inches", "24 inches"], correct: 2, explanation: "MUTCD requires a minimum 18-inch STOP/SLOW paddle for all flagging operations." },
    ],
  },
  {
    num: 8, icon: "📻", time: "20 min",
    title: "Communication Protocols",
    objectives: [
      "Know CB radio channel assignments",
      "Understand lead/rear escort communication requirements",
      "Know pre-departure communication checklist items",
      "Understand when state agencies may require their own channel",
    ],
    blocks: [
      { type: "para", text: "CB radio communication is mandatory for all escort operations. Channel 19 is the national default unless the permit specifies otherwise; Channel 9 is emergency-only." },
      { type: "para", text: "Pre-departure checklist: establish Channel 19 contact with the carrier driver, confirm the channel with the rear escort if applicable, test radio range over the initial route segment, and agree on stop code words." },
      { type: "tip", title: "Exam Tip", text: "Know that Channel 19 is the universal default and Channel 9 is reserved for emergencies — this distinction is tested in nearly every state exam." },
    ],
    practice: [
      { q: "What is the default CB radio channel for escort operations?", options: ["Channel 9", "Channel 17", "Channel 19", "Channel 21"], correct: 2, explanation: "Channel 19 is the default channel for escort operations nationwide unless the permit specifies otherwise." },
    ],
  },
  {
    num: 9, icon: "🚗", time: "25 min",
    title: "Trip Operations — Lead Escort",
    objectives: [
      "Know lead escort duties and standard positioning",
      "Understand basic intersection clearance procedures",
      "Know when urban corridors require increased following distance",
      "Understand when state-specific procedures apply",
    ],
    blocks: [
      { type: "para", text: "The lead escort is the advance warning system for the oversize load. Standard positioning is a minimum of 500 feet ahead of the load on open highway." },
      { type: "para", text: "Intersection clearance: confirm all conflicting traffic is stopped or cleared, radio the carrier that the intersection is clear, hold position until the rear of the load clears, then resume the standard gap." },
      { type: "tip", title: "Exam Tip", text: "The 500-foot lead escort gap is a national standard — know that some states and urban corridors require a larger gap." },
    ],
    practice: [
      { q: "What is the standard minimum lead escort gap on open highway?", options: ["100 feet", "250 feet", "500 feet", "1,000 feet"], correct: 2, explanation: "The standard minimum lead escort gap is 500 feet ahead of the load on open highway." },
    ],
  },
  {
    num: 10, icon: "🚙", time: "20 min",
    title: "Trip Operations — Rear Escort",
    objectives: [
      "Know rear escort positioning and duties",
      "Understand how rear escorts monitor the load",
      "Know how to deter overtaking traffic",
      "Understand when state-specific rear escort rules apply",
    ],
    blocks: [
      { type: "para", text: "The rear escort protects the load from behind and reports on conditions behind the load. Standard positioning is 100 to 300 feet behind the trailing end of the load on open highway." },
      { type: "para", text: "Rear escort duties: monitor the load for shift, loose straps, or debris; deter following traffic from passing using amber lights, the OVERSIZE LOAD sign, and CB radio; and monitor overhead clearance from the best rear vantage point." },
      { type: "tip", title: "Exam Tip", text: "Know the standard rear escort gap range (100–300 ft) and that narrower two-lane roads often call for the closer end of that range." },
    ],
    practice: [
      { q: "What is the standard rear escort positioning range behind the load?", options: ["25–50 feet", "100–300 feet", "500–750 feet", "1,000+ feet"], correct: 1, explanation: "Standard rear escort positioning is 100 to 300 feet behind the trailing end of the load." },
    ],
  },
  {
    num: 11, icon: "🚨", time: "25 min",
    title: "Safety & Emergency Procedures",
    objectives: [
      "Know general wind, weather, and visibility restrictions",
      "Understand standard breakdown procedures",
      "Know general accident reporting principles",
      "Understand when and how to abort a move",
    ],
    blocks: [
      { type: "para", text: "Most oversize permits prohibit moves when sustained winds exceed roughly 35 mph, with a lower threshold for tall or sail-type loads. Moves must be suspended when visibility drops below about 500 feet due to fog, rain, or smoke." },
      { type: "para", text: "If a breakdown occurs: activate all warning lights, move to the nearest safe pull-off or shoulder, place warning triangles at 100/200/300 feet behind the stopped load, and contact the carrier and the relevant state agency. Remain with the load until properly relieved." },
      { type: "tip", title: "Exam Tip", text: "Know the general wind (35 mph) and visibility (500 ft) thresholds — most states use similar numbers, though some set stricter limits." },
    ],
    practice: [
      { q: "At approximately what sustained wind speed do most oversize permits require a move to be suspended?", options: ["15 mph", "25 mph", "35 mph", "55 mph"], correct: 2, explanation: "Most oversize permits suspend moves at sustained winds exceeding approximately 35 mph, with lower thresholds often set for sail-type loads." },
    ],
  },
  {
    num: 12, icon: "📋", time: "15 min",
    title: "Post-Trip Procedures & Documentation",
    objectives: [
      "Complete a thorough post-trip inspection",
      "Know general documentation and retention principles",
      "Understand general incident reporting obligations",
      "Know how to handle permit close-out",
    ],
    blocks: [
      { type: "para", text: "After every move, inspect equipment for damage, and document the move: start/end time, actual route traveled, any incidents or near-misses, and road hazards for future surveys." },
      { type: "key", title: "Key Point", text: "Most states require permit and inspection records to be retained for a minimum of several years — verify your state's exact retention period." },
      { type: "tip", title: "Exam Tip", text: "Know that any accident during a permitted move must be reported to the state permitting authority, typically within 24 hours." },
    ],
    practice: [
      { q: "What should be documented immediately after every oversize move?", options: ["Nothing, unless an accident occurred", "Move start/end time, route traveled, and any incidents", "Only the carrier's fuel receipts", "Only the escort operator's personal notes"], correct: 1, explanation: "Post-trip documentation should include move start/end time, actual route traveled, and any incidents or near-misses." },
    ],
  },
  {
    num: 13, icon: "🎓", time: "20 min",
    title: "State Certification & Permitting",
    objectives: [
      "Understand the general certification and permitting process",
      "Know common eligibility requirements across states",
      "Know general exam format expectations",
      "Understand that fees, scores, and renewal periods vary by state",
    ],
    blocks: [
      { type: "para", text: "Most states share common eligibility elements: a minimum age of 18, a valid driver's license, proof of liability insurance, and a passing score on a state or national exam." },
      { type: "key", title: "Key Point", text: "Insurance minimums, passing scores, exam length, and renewal periods all vary by state — this module is where each state's specific numbers replace this general baseline." },
      { type: "tip", title: "Exam Tip", text: "Memorize your specific state's numbers (passing score, insurance minimum, fee, retake wait) — these are almost always tested directly." },
    ],
    practice: [
      { q: "Which of the following typically varies from state to state?", options: ["The existence of an exam", "Insurance minimum and passing score", "Whether a driver's license is required", "Whether the operator must be an adult"], correct: 1, explanation: "While most states share the general structure of certification, the specific insurance minimum and passing score are state-specific." },
    ],
  },
  {
    num: 14, icon: "📝", time: "30 min",
    title: "Final Exam Prep & Practice",
    objectives: [
      "Review all critical federal numbers and specifications",
      "Practice full-length exam-style questions",
      "Identify common trick questions and errors",
      "Build exam-day confidence with a review strategy",
    ],
    blocks: [
      { type: "para", text: "Exams consistently test specific numerical values — federal dimension limits, equipment specifications, and escort positioning — alongside whatever numbers are unique to your state." },
      { type: "key", title: "Key Point", text: "Read every exam question twice. Watch for the specific agency named in the question — different agencies can have different authority in the same scenario." },
      { type: "tip", title: "Exam Tip", text: "Spend the most study time on your state's unique rules — federal MUTCD/FHWA content is the same everywhere, but state-specific rules are what typically separate a pass from a retake." },
    ],
    practice: [
      { q: "Which type of exam question is most commonly missed by candidates?", options: ["Federal dimension questions", "Questions unique to their specific state", "Basic vocabulary questions", "Questions about vehicle color"], correct: 1, explanation: "State-specific rules are the most commonly missed because they can't be answered from federal FHWA/MUTCD knowledge alone." },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────
// STATE_OVERRIDES — only what's DIFFERENT for a given state. Each
// entry adds extra blocks/practice questions on top of the matching
// BASE_MODULES module, plus state meta and extra quick-reference
// cards. Add a new state here without touching BASE_MODULES.
// ─────────────────────────────────────────────────────────────────
const STATE_OVERRIDES = {
  WA: {
    name: "Washington", flag: "🌲", agency: "WSDOT",
    meta: { width: "12'0\"", height: "14'0\"", length: "75'0\"", ins: "$500,000 CSL", night: "No Fri 3pm–Mon 6am", cert: "WSDOT Pilot Car Operator Permit" },
    quickref: [
      { h: "Washington Escort Triggers", rows: [["Permit required", "Over 8'6\""], ["One escort (lead)", "Over 12'0\""], ["Two escorts (lead+rear)", "Over 14'0\""], ["WSP escort required", "Over 16'0\" / 200,000 lbs"]] },
      { h: "Washington Certification", rows: [["Exam passing score", "70% (35/50)"], ["Annual renewal fee", "$95"], ["Insurance minimum", "$500,000 CSL"], ["Retake wait", "14 days"], ["Permit expiration", "Midnight, last date"]] },
      { h: "Ferry & Contacts", rows: [["WSF reservation window", "48 hours advance"], ["Arrive before departure", "30 minutes"], ["WSDOT CVS", "(360) 704-6340"], ["Washington State Patrol", "*WSP / (360) 596-4000"], ["WSF Commercial Vehicle", "(206) 515-3952"]] },
    ],
    modules: {
      1: {
        blocks: [
          { type: "key", title: "WA Key Point", text: "Washington is one of 8 states requiring a state-specific permit (WSDOT Pilot Car Operator Permit) IN ADDITION to PEVO certification. PEVO alone is not sufficient." },
          { type: "key", title: "WA Key Point", text: "Washington State Patrol (WSP) escort is required for loads over 16 feet wide OR over 200,000 lbs gross weight — separate from standard pilot car escorts." },
          { type: "warn", title: "WA Warning", text: "Operating without a valid WSDOT Pilot Car Operator Permit can result in fines, permit revocation, and personal liability if an accident occurs during the move." },
        ],
        practice: [
          { q: "At what load width does Washington require a WSP escort in addition to pilot car escorts?", options: ["Over 12 feet", "Over 14 feet", "Over 16 feet", "Over 18 feet"], correct: 2, explanation: "Washington requires a WSP escort for loads over 16 feet wide or over 200,000 lbs gross weight." },
          { q: "Which agency administers OS/OW permitting and pilot car certification in Washington?", options: ["FHWA", "Washington State Patrol (WSP)", "WSDOT Commercial Vehicle Services (CVS)", "National Pilot Car Association (NPCA)"], correct: 2, explanation: "WSDOT Commercial Vehicle Services (CVS) administers all OS/OW permitting and certification in Washington." },
        ],
      },
      2: {
        blocks: [
          { type: "para", text: "Washington has adopted the federal Bridge Formula for all state highways, and layers on bridge-specific limits: the SR-520 floating bridge caps gross weight at approximately 105,000 lbs, and the I-90 floating bridge caps gross weight at approximately 130,000 lbs in designated lanes." },
          { type: "key", title: "WA Key Point", text: "Floating bridge weight limits for SR-520 and I-90 are not printed on a standard permit — verify separately with WSDOT engineering before any heavy-load move." },
        ],
        practice: [
          { q: "What is the maximum gross weight allowed on the SR-520 floating bridge without special engineering authorization?", options: ["80,000 lbs", "100,000 lbs", "105,000 lbs", "No weight restriction applies"], correct: 2, explanation: "The SR-520 floating bridge caps gross weight at approximately 105,000 lbs without separate WSDOT engineering authorization." },
        ],
      },
      3: {
        blocks: [
          { type: "key", title: "WA Key Point", text: "Washington requires the OVERSIZE LOAD sign to also be displayed on the rear of the load for widths over 14 feet, in addition to the standard front placement." },
          { type: "warn", title: "WA Warning", text: "On Washington State Ferry decks, ANSI Class 3 apparel is required regardless of vehicle position, amber lights must remain active, and you must coordinate directly with WSF crew." },
        ],
        practice: [
          { q: "What ANSI vest class is required on a Washington State Ferry deck during loading?", options: ["Class 1", "Class 2", "Class 3", "No vest required on ferries"], correct: 2, explanation: "Washington requires ANSI Class 3 apparel on all Washington State Ferry decks during loading/unloading, regardless of vehicle position." },
        ],
      },
      4: {
        blocks: [
          { type: "key", title: "WA Key Point", text: "Washington oversize permits expire at midnight on the last authorized date — a hard deadline with no grace period." },
          { type: "key", title: "WA Key Point", text: "Ferry crossings must be explicitly authorized within the permit. A permit that does not mention a ferry crossing does not authorize boarding one." },
        ],
        practice: [
          { q: "At what time do Washington oversize permits expire on the last authorized date?", options: ["5:00 PM", "10:00 PM", "Midnight (12:00 AM)", "The following morning at 6:00 AM"], correct: 2, explanation: "Washington oversize permits expire at midnight on the last authorized date, with no grace period." },
        ],
      },
      5: {
        blocks: [
          { type: "warn", title: "WA Warning", text: "SR-20 North Cascades Highway closes seasonally, approximately late November to mid-April. Oversize moves are never permitted during that closure window." },
          { type: "key", title: "WA Key Point", text: "Contact Washington State Ferries (WSF) Commercial Vehicle reservations at least 48 hours before any ferry crossing at (206) 515-3952." },
        ],
        practice: [
          { q: "Which Washington mountain pass closes seasonally and bars oversize moves during the closure?", options: ["US-2 Stevens Pass", "US-12 White Pass", "SR-20 North Cascades Highway", "I-90 Snoqualmie Pass"], correct: 2, explanation: "SR-20 North Cascades Highway closes seasonally, approximately late November to mid-April." },
        ],
      },
      6: {
        blocks: [
          { type: "key", title: "WA Key Point", text: "Your WSDOT Pilot Car Operator Permit card must be physically present in the vehicle at every stop — operating without it is a violation even if your permit is current." },
          { type: "para", text: "For routes on US-2, US-12, or SR-20 between October and April, tire chains must be in the vehicle before departure." },
        ],
        practice: [
          { q: "Which document must be physically present in the escort vehicle during a permitted move in Washington?", options: ["A printout of the WSDOT website", "WSDOT Pilot Car Operator Permit card", "NPCA certification card", "The carrier's overweight permit only"], correct: 1, explanation: "The WSDOT Pilot Car Operator Permit card must be physically present and available for inspection at all times." },
        ],
      },
      7: {
        blocks: [
          { type: "warn", title: "WA Warning", text: "On Washington State Ferry decks, WSF crew authority supersedes your escort authority. Never position a STOP/SLOW paddle in a way that conflicts with WSF crew direction." },
        ],
        practice: [
          { q: "On a Washington State Ferry deck, whose authority supersedes the escort operator's traffic control signals?", options: ["The carrier driver's authority", "Washington State Patrol authority", "Washington State Ferries crew authority", "The lead escort's authority"], correct: 2, explanation: "On Washington State Ferry decks, WSF crew authority supersedes all other direction, including pilot car escort signals." },
        ],
      },
      8: {
        blocks: [
          { type: "key", title: "WA Key Point", text: "WSP authority supersedes all escort operator authority. If WSP calls a stop or changes the route, all escorts comply immediately." },
          { type: "warn", title: "WA Warning", text: "For WSP-escorted moves, the move cannot begin until the WSP officer gives explicit authorization at the pre-move briefing." },
        ],
        practice: [
          { q: "For a Washington move requiring WSP escort, when may the convoy begin moving?", options: ["When all escorts are in position", "When the carrier driver is ready", "Only after WSP gives explicit authorization at the pre-move briefing", "As soon as the permit is verified"], correct: 2, explanation: "WSP-escorted moves in Washington cannot begin until the WSP officer gives explicit authorization." },
        ],
      },
      9: {
        blocks: [
          { type: "para", text: "In Seattle, Tacoma, and Spokane urban corridors, increase the lead escort gap to 1,000 feet. At Washington State Ferry terminals, arrive at least 30 minutes before departure, check in with the WSF terminal agent, and board first in the designated lane." },
        ],
        practice: [
          { q: "How early should the lead escort arrive at a Washington ferry terminal before scheduled departure?", options: ["10 minutes", "15 minutes", "30 minutes minimum", "The day before"], correct: 2, explanation: "Lead escorts with ferry crossings in Washington must arrive at least 30 minutes before scheduled departure." },
        ],
      },
      10: {
        blocks: [
          { type: "key", title: "WA Key Point", text: "On Washington State Ferries, the rear escort loads LAST onto the ferry and disembarks FIRST, so the convoy can reassemble on the departure road." },
        ],
        practice: [
          { q: "On a Washington ferry crossing with two escort vehicles, in what order do the escorts load onto the ferry?", options: ["Lead escort last, rear escort first", "Lead escort first, then load, then rear escort last", "Both escorts board simultaneously", "Rear escort first, lead escort last"], correct: 1, explanation: "In Washington: lead escort boards first, then the load, then the rear escort boards last." },
        ],
      },
      11: {
        blocks: [
          { type: "para", text: "Washington's wind and visibility thresholds match the national baseline: 35 mph sustained wind (25 mph for sail-type loads), 500 ft minimum visibility. On mountain passes, if a breakdown occurs, move to the next available pullout and call *WSP." },
          { type: "key", title: "WA Key Point", text: "Washington State Ferries can deny oversize load boarding based on sea state — this decision is final and cannot be overridden by a WSDOT permit." },
        ],
        practice: [
          { q: "What is the Washington State Patrol contact for non-emergency assistance during a breakdown?", options: ["911", "*WSP or (360) 596-4000", "(360) 704-6340 (WSDOT CVS)", "(206) 515-3952 (WSF)"], correct: 1, explanation: "Washington State Patrol non-emergency contact is *WSP (from a cell phone) or (360) 596-4000." },
        ],
      },
      12: {
        blocks: [
          { type: "warn", title: "WA Warning", text: "Any accident during a Washington permitted move must be reported to WSDOT CVS within 24 hours — even if WSP was already notified at the scene." },
          { type: "key", title: "WA Key Point", text: "Washington requires permit and inspection records to be retained for a minimum of 3 years." },
        ],
        practice: [
          { q: "Within what timeframe must an accident during a Washington oversize move be reported to WSDOT CVS?", options: ["Immediately at the scene", "Within 24 hours", "Within 72 hours", "Within 7 days"], correct: 1, explanation: "Accidents during Washington permitted moves must be reported to WSDOT CVS within 24 hours." },
        ],
      },
      13: {
        blocks: [
          { type: "para", text: "Washington eligibility: any state driver's license (no WA license required), minimum age 18, $500,000 CSL insurance minimum, no DUI within 3 years, 70% passing score, and a $95 annual fee." },
          { type: "key", title: "WA Key Point", text: "Washington accepts a driver's license from ANY state — a Washington state license is not required to obtain the WSDOT permit." },
        ],
        practice: [
          { q: "What is the minimum passing score for the Washington WSDOT Pilot Car Operator exam?", options: ["65%", "70%", "75%", "80%"], correct: 1, explanation: "The Washington WSDOT exam requires a 70% minimum passing score (35 of 50 questions)." },
        ],
      },
      14: {
        blocks: [
          { type: "para", text: "Common Washington trick questions: PEVO alone is NOT sufficient — the WSDOT permit is required in addition. Permits expire at midnight, not end of business day. On a ferry deck, WSF crew authority always supersedes the escort operator's. SR-20 is NOT open year-round." },
        ],
        practice: [
          { q: "True or False: national PEVO certification alone is legally sufficient to operate as an escort in Washington.", options: ["True — PEVO is nationally recognized", "False — Washington requires the WSDOT Pilot Car Operator Permit in addition to PEVO", "True — for loads under 14 feet wide only", "False — Washington does not recognize PEVO at all"], correct: 1, explanation: "Washington is one of 8 states requiring a state-specific permit in addition to PEVO." },
          { q: "SR-20 North Cascades Highway is closed to oversize moves during which period?", options: ["Only during active snowfall events", "Every weekend from October to May", "Seasonally, approximately late November to mid-April", "Only on holidays"], correct: 2, explanation: "SR-20 closes seasonally from approximately late November to mid-April." },
        ],
      },
    },
  },

  CA: {
    name: "California", flag: "🌉", agency: "Caltrans",
    meta: { width: "14'0\"", height: "14'0\"", length: "95'0\"", ins: "$750,000 CSL", night: "No Fri 3pm–Mon 6am", cert: "Caltrans Permit + CHP-Approved Escort Cert" },
    quickref: [
      { h: "California Escort Triggers", rows: [["One escort (lead)", "Over ~10'0\""], ["Two escorts (lead+rear)", "Over ~12'0\""], ["CHP escort likely", "Over 14'0\" / very heavy loads"]] },
      { h: "California Certification", rows: [["Training", "CHP-approved escort course"], ["Insurance minimum", "$750,000 CSL"], ["Permit issuer", "Caltrans"]] },
    ],
    modules: {
      1: {
        blocks: [
          { type: "key", title: "CA Key Point", text: "California requires escort operators to complete a CHP-approved (California Highway Patrol) training course in addition to holding a valid Caltrans transportation permit for the load." },
        ],
        practice: [
          { q: "Which agency issues oversize load transportation permits in California?", options: ["CHP directly", "Caltrans", "DMV", "FHWA"], correct: 1, explanation: "Caltrans (California Department of Transportation) issues transportation permits; CHP handles escort training approval and police escorts." },
        ],
      },
      4: {
        blocks: [
          { type: "warn", title: "CA Warning", text: "California prohibits most oversize moves from Friday 3:00 PM to Monday 6:00 AM, with additional blackout windows around major holidays (Memorial Day, July 4th, Labor Day, Thanksgiving, Christmas, New Year's). Width is irrelevant — all permitted loads are restricted during these windows." },
        ],
        practice: [
          { q: "When does California's standard weekend travel restriction for oversize loads begin?", options: ["Friday 3:00 PM", "Saturday 12:00 AM", "Friday midnight", "Saturday 6:00 AM"], correct: 0, explanation: "California's weekend restriction typically runs from Friday 3:00 PM to Monday 6:00 AM on most routes." },
        ],
      },
      13: {
        blocks: [
          { type: "para", text: "California eligibility generally includes CHP-approved escort training, proof of $750,000 CSL insurance, and passing the required exam. Certification and permit renewal timing should always be verified directly with Caltrans and CHP, as requirements are updated periodically." },
        ],
        practice: [],
      },
    },
  },

  OR: {
    name: "Oregon", flag: "🌲", agency: "ODOT",
    meta: { width: "14'6\"", height: "15'6\"", length: "95'0\"", ins: "$750,000 CSL", night: "Restricted", cert: "ODOT Permit" },
    quickref: [
      { h: "Oregon Notes", rows: [["Permit issuer", "ODOT"], ["Height allowance", "Up to 15'6\" (higher than most states)"], ["Urban routing", "I-5 through Portland has timing restrictions"]] },
    ],
    modules: {
      1: {
        blocks: [
          { type: "para", text: "Oregon's ODOT issues route-specific transportation permits. Oregon allows a taller standard height threshold (15'6\") than most surrounding states, which is a common point of confusion for escorts used to a 14'6\" ceiling." },
        ],
        practice: [
          { q: "Which is notable about Oregon's standard height allowance compared to most neighboring states?", options: ["It is lower, at 12'0\"", "It is higher, at 15'6\"", "There is no height limit", "It matches the federal minimum exactly"], correct: 1, explanation: "Oregon's standard height allowance of 15'6\" is higher than the 14'6\" ceiling common in many neighboring states." },
        ],
      },
      5: {
        blocks: [
          { type: "para", text: "Routes through the Portland I-5 corridor may carry timing restrictions during peak traffic hours. Always check ODOT's current route conditions before planning a move through the metro area." },
        ],
        practice: [],
      },
    },
  },

  TN: {
    name: "Tennessee", flag: "🎵", agency: "TDOT",
    meta: { width: "14'6\"", height: "15'0\"", length: "95'0\"", ins: "$750,000 CSL", night: "w/ lighting", cert: "No separate cert — TDOT permit only" },
    quickref: [
      { h: "Tennessee Notes", rows: [["Permit issuer", "TDOT"], ["Separate state cert", "Not required — permit only"], ["Terrain note", "Mountainous routes (I-40 Cumberland Gap area) warrant extra route survey time"]] },
    ],
    modules: {
      1: {
        blocks: [
          { type: "para", text: "Tennessee does not require a separate state-specific escort certification beyond a valid national PEVO certificate — TDOT issues the load's transportation permit directly." },
        ],
        practice: [
          { q: "Does Tennessee require a separate state-issued escort certification beyond a national PEVO certificate?", options: ["Yes, always", "No — a valid PEVO certificate plus the TDOT permit is sufficient", "Only for loads over 100,000 lbs", "Only in Nashville"], correct: 1, explanation: "Tennessee does not require its own separate escort certification; a valid PEVO certificate plus the TDOT-issued permit is sufficient." },
        ],
      },
      5: {
        blocks: [
          { type: "para", text: "Mountainous stretches such as I-40 through the Cumberland Gap area warrant additional route survey time — grade, curve radius, and weather can all affect an oversize move more than on flatter interstate segments." },
        ],
        practice: [],
      },
    },
  },

  TX: {
    name: "Texas", flag: "⭐", agency: "TxDOT",
    meta: { width: "16'0\"", height: "16'0\"", length: "110'0\"", ins: "$750,000 CSL", night: "Allowed", cert: "TxDOT Escort License (2-year)" },
    quickref: [
      { h: "Texas Escort Triggers", rows: [["Front escort required", "Over 14'0\" wide"], ["Front + rear escort", "Over 16'0\" wide"], ["DPS police escort", "Over 20'0\" wide or 200,000 lbs"]] },
      { h: "Texas Certification", rows: [["License type", "TxDOT Escort License"], ["Validity", "2 years"], ["Insurance minimum", "$750,000 CSL"]] },
    ],
    modules: {
      1: {
        blocks: [
          { type: "key", title: "TX Key Point", text: "Texas requires its own TxDOT Escort License, valid for 2 years — a national PEVO certificate alone does not authorize escort operations in Texas." },
        ],
        practice: [
          { q: "What license does Texas require for pilot/escort vehicle operators, separate from national PEVO certification?", options: ["TxDOT Escort License", "Texas DPS Commercial License", "FHWA Interstate Permit", "No separate license is required"], correct: 0, explanation: "Texas requires its own TxDOT Escort License, valid for 2 years, in addition to (or instead of) national PEVO certification." },
        ],
      },
      2: {
        blocks: [
          { type: "para", text: "Texas allows larger standard dimensions than most states: up to 16'0\" wide, 16'0\" tall, and 110'0\" long before additional superload procedures apply — noticeably larger than the more common 14'6\" / 95'0\" baseline." },
        ],
        practice: [
          { q: "At what width does Texas require BOTH a front and rear escort?", options: ["Over 12'0\"", "Over 14'0\"", "Over 16'0\"", "Over 20'0\""], correct: 2, explanation: "Texas requires a front escort over 14'0\" wide, and both front and rear escorts over 16'0\" wide." },
        ],
      },
      11: {
        blocks: [
          { type: "warn", title: "TX Warning", text: "Loads over 20'0\" wide or 200,000 lbs require a Texas DPS (Department of Public Safety) police escort in addition to civilian pilot cars." },
        ],
        practice: [],
      },
    },
  },

  WI: {
    name: "Wisconsin", flag: "🧀", agency: "WisDOT",
    meta: { width: "14'6\"", height: "15'0\"", length: "95'0\"", ins: "$750,000 CSL", night: "w/ lighting", cert: "No cert — WisDOT permit only" },
    quickref: [
      { h: "Wisconsin Notes", rows: [["Permit issuer", "WisDOT"], ["Seasonal note", "Spring frost-law weight restrictions apply on many state routes"]] },
    ],
    modules: {
      2: {
        blocks: [
          { type: "warn", title: "WI Warning", text: "Wisconsin enforces seasonal frost-law weight restrictions on many state routes during spring thaw — posted weight limits can drop significantly below normal for several weeks. Always check current WisDOT frost-law status before a heavy move." },
        ],
        practice: [
          { q: "What seasonal restriction is unique to Wisconsin route planning?", options: ["Summer heat load restrictions", "Spring frost-law weight restrictions", "Winter width restrictions", "Fall lighting restrictions"], correct: 1, explanation: "Wisconsin posts reduced weight limits on many routes during spring frost-law season to protect roads during thaw." },
        ],
      },
    },
  },

  WY: {
    name: "Wyoming", flag: "🦬", agency: "WYDOT",
    meta: { width: "14'6\"", height: "14'6\"", length: "95'0\"", ins: "$750,000 CSL", night: "Allowed", cert: "No cert — WYDOT permit only" },
    quickref: [
      { h: "Wyoming Notes", rows: [["Permit issuer", "WYDOT"], ["Wind note", "High-profile loads frequently restricted during wind advisories, especially I-80"]] },
    ],
    modules: {
      11: {
        blocks: [
          { type: "warn", title: "WY Warning", text: "Wyoming is prone to sustained high winds, especially along I-80. High-profile loads (enclosed trailers, manufactured homes) are frequently restricted or grounded during wind advisories — check WYDOT road conditions before and during a move." },
        ],
        practice: [
          { q: "Which Wyoming corridor is especially known for high-wind travel restrictions on high-profile loads?", options: ["I-25", "I-80", "I-90", "US-20"], correct: 1, explanation: "I-80 across Wyoming is especially prone to sustained high winds that frequently restrict high-profile oversize loads." },
        ],
      },
    },
  },

  NY: {
    name: "New York", flag: "🗽", agency: "NYSDOT",
    meta: { width: "16'0\"", height: "15'0\"", ins: "$1,000,000 CSL", night: "Restricted", cert: "NYSDOT Required — zero reciprocity" },
    quickref: [
      { h: "New York Notes", rows: [["Reciprocity", "None — NYSDOT credential mandatory"], ["Insurance minimum", "$1,000,000 CSL"], ["Police escort", "Required for loads over 13'6\" wide"], ["Fines", "Start at $5,000 per violation"]] },
    ],
    modules: {
      1: {
        blocks: [
          { type: "warn", title: "NY Warning", text: "New York has zero reciprocity with national PEVO certification or other states' escort credentials. A NYSDOT-specific credential is mandatory — no substitutes are accepted." },
        ],
        practice: [
          { q: "Does New York accept a national PEVO certificate as sufficient for escort operations?", options: ["Yes, without exception", "No — NYSDOT requires its own specific credential", "Only for loads under 12 feet wide", "Only with a co-signature from another state"], correct: 1, explanation: "New York has zero reciprocity — a NYSDOT-specific credential is mandatory regardless of PEVO status." },
        ],
      },
      13: {
        blocks: [
          { type: "key", title: "NY Key Point", text: "New York's insurance minimum is $1,000,000 CSL — notably higher than the common $750,000 CSL baseline. Fines for violations start at $5,000 per incident." },
        ],
        practice: [
          { q: "What is New York's minimum liability insurance requirement (CSL)?", options: ["$500,000", "$750,000", "$1,000,000", "$2,000,000"], correct: 2, explanation: "New York requires a minimum of $1,000,000 CSL, higher than the $750,000 CSL common in most other states." },
        ],
      },
      11: {
        blocks: [
          { type: "warn", title: "NY Warning", text: "Police escort is required in New York for loads wider than 13'6\" — narrower than the 14'-16' thresholds common elsewhere." },
        ],
        practice: [],
      },
    },
  },

  DC: {
    name: "Washington, D.C.", flag: "🏛️", agency: "DDOT",
    meta: { width: "13'0\"", height: "13'6\"", ins: "$1,000,000 CSL", night: "Restricted", cert: "DDOT Permit" },
    quickref: [
      { h: "Washington, D.C. Notes", rows: [["Permit issuer", "DDOT"], ["Extra coordination", "May require National Park Service and/or Capitol Police, depending on route"], ["Insurance minimum", "$1,000,000 CSL"]] },
    ],
    modules: {
      1: {
        blocks: [
          { type: "warn", title: "DC Warning", text: "Washington, D.C. routes may require coordination with DDOT plus the National Park Service and/or Capitol Police, depending on whether the route passes federal land or restricted zones. Move windows are extremely restricted." },
        ],
        practice: [
          { q: "Besides DDOT, which additional agencies might a D.C. oversize route require coordination with?", options: ["FEMA and the Coast Guard", "National Park Service and/or Capitol Police, depending on route", "The Smithsonian Institution", "No other agency is ever involved"], correct: 1, explanation: "Depending on the route, D.C. moves may require coordination with the National Park Service and/or Capitol Police in addition to DDOT." },
        ],
      },
    },
  },

  HI: {
    name: "Hawaii", flag: "🌺", agency: "HDOT",
    meta: { width: "10'0\"", height: "14'0\"", ins: "$750,000 CSL", night: "Allowed", cert: "HDOT Permit" },
    quickref: [
      { h: "Hawaii Notes", rows: [["Width ceiling", "10'0\" — well below the mainland 14' baseline"], ["Inter-island moves", "May require barge transport between islands"]] },
    ],
    modules: {
      2: {
        blocks: [
          { type: "warn", title: "HI Warning", text: "Hawaii's island road infrastructure limits most moves to just 10'0\" wide — dramatically below the mainland baseline of 14'0\"–14'6\". Operators trained on mainland numbers frequently overlook this constraint." },
        ],
        practice: [
          { q: "What is Hawaii's typical maximum width allowance, notably lower than most mainland states?", options: ["14'0\"", "12'0\"", "10'0\"", "8'6\" (federal minimum only)"], correct: 2, explanation: "Hawaii's road infrastructure typically limits oversize moves to 10'0\" wide, well below the 14'-14'6\" baseline common on the mainland." },
        ],
      },
      5: {
        blocks: [
          { type: "para", text: "Moves between Hawaiian islands may require barge transport rather than a continuous road route — factor vessel schedules and port handling into any multi-island route plan." },
        ],
        practice: [],
      },
    },
  },
};

const DETAILED_STATES = new Set(["WA", "CA", "OR", "TN", "TX", "WI", "WY", "NY", "DC", "HI"]);

// ─────────────────────────────────────────────────────────────────
// LIGHTWEIGHT STATE OVERRIDES — the "same block" system.
//
// The 10 states above (WA, CA, OR, TN, TX, WI, WY, NY, DC, HI) have
// fully custom overrides with rules woven into several modules.
//
// Every other state doesn't need its own hand-written block — this
// generator builds one consistent override from three inputs (state
// code, permitting agency, one regional note) and reuses it for all
// of them. It still touches Module 1 (agency + practice question)
// and Module 13 (eligibility) so the course reads as state-specific,
// without maintaining 41 separate hand-built module sets.
//
// To give a state real depth later, don't edit this table — copy the
// WA/CA/TX pattern above, add `<CODE>: { name, flag, agency, meta,
// quickref, modules: {...} }` before this block, and it will
// automatically take priority over the generated version below.
// ─────────────────────────────────────────────────────────────────
function makeSimpleOverride(code, agency, note) {
  const meta = findStateMeta(code);
  const label = meta?.name || code;
  return {
    name: label,
    flag: STATE_FLAGS[code] || "🏳️",
    agency,
    meta: meta ? { width: meta.width, height: meta.height, length: meta.length, ins: meta.ins, night: meta.night, cert: meta.cert } : {},
    quickref: [
      { h: `${label} Notes`, rows: [["Permit issuer", agency], ["Insurance minimum", meta?.ins || "Verify with state"], ["Cert path", meta?.cert || "Verify with state"]] },
    ],
    modules: {
      1: {
        blocks: [
          { type: "key", title: `${code} Key Point`, text: `${agency} administers oversize/overweight permitting in ${label}. ${note}` },
        ],
        practice: [
          { q: `Which agency administers oversize/overweight permitting in ${label}?`, options: [agency, "FHWA", "Department of Motor Vehicles (DMV)", "State Highway Patrol"], correct: 0, explanation: `${agency} is the permitting authority for oversize/overweight loads in ${label}.` },
        ],
      },
      13: {
        blocks: [
          { type: "para", text: `${label} eligibility generally follows the group baseline: minimum age 18, a valid driver's license, and proof of ${meta?.ins || "the required"} liability insurance. Always verify current fees and exam requirements directly with ${agency}.` },
        ],
        practice: [],
      },
    },
  };
}

const SIMPLE_STATE_DATA = [
  // 14 PEVO states — accept a national PEVO cert, no separate state permit
  ["AL", "ALDOT", "Alabama accepts a national PEVO certificate as sufficient — no separate state escort permit is required. Gulf Coast hurricane season can trigger temporary move suspensions."],
  ["AR", "ARDOT", "Arkansas accepts a national PEVO certificate as sufficient. Ozark region mountain routes in the north/west may need extra route survey time."],
  ["CO", "CDOT", "Colorado accepts a national PEVO certificate as sufficient. Mountain passes (I-70 through the Rockies) commonly trigger winter chain-law requirements."],
  ["CT", "ConnDOT", "Connecticut accepts a national PEVO certificate as sufficient. Dense I-95 corridors near New Haven and Bridgeport often carry move-timing restrictions."],
  ["DE", "DelDOT", "Delaware accepts a national PEVO certificate as sufficient. Delaware's small size means most moves quickly cross into Maryland, New Jersey, or Pennsylvania — plan multi-state permits accordingly."],
  ["FL", "FDOT", "Florida accepts a national PEVO certificate, but additionally requires a defensive-driving certificate before escort operations."],
  ["GA", "GDOT", "Georgia accepts a national PEVO certificate as sufficient. Atlanta metro congestion often dictates off-peak move windows."],
  ["KY", "KYTC", "Kentucky accepts a national PEVO certificate as sufficient. Eastern Appalachian mountain routes may need extra route survey time."],
  ["LA", "LADOTD", "Louisiana accepts a national PEVO certificate as sufficient. Hurricane season and low-lying, flood-prone routes can trigger move suspensions."],
  ["MI", "MDOT", "Michigan accepts a national PEVO certificate as sufficient. Lake-effect snow can suspend moves with little advance notice."],
  ["MS", "MDOT", "Mississippi accepts a national PEVO certificate as sufficient. Gulf Coast hurricane season is a key seasonal consideration, similar to Alabama and Louisiana."],
  ["NC", "NCDOT", "North Carolina accepts a national PEVO certificate as sufficient. Coastal hurricane evacuation routes may restrict oversize moves during storm events."],
  ["NM", "NMDOT", "New Mexico accepts a national PEVO certificate as sufficient. High desert wind and long rural distances between services are key route-planning factors."],
  ["SC", "SCDOT", "South Carolina accepts a national PEVO certificate as sufficient. Coastal hurricane season and holiday beach-corridor traffic (I-26/I-95) can affect timing."],

  // 8-State Group (minus Washington, which has its own full override above)
  ["AZ", "ADOT", "Arizona uses the shared 8-state equipment and dimension baseline, with no separate state cert. Extreme summer heat and monsoon dust storms are key seasonal factors."],
  ["ID", "ITD", "Idaho uses the shared 8-state baseline, with no separate state cert. Mountain pass routes may need extra survey time."],
  ["IA", "IaDOT", "Iowa uses the shared 8-state baseline, with no separate state cert. Fall harvest season brings heavy grain-truck traffic to rural routes."],
  ["KS", "KDOT", "Kansas uses the shared 8-state baseline, with no separate state cert. High-wind plains corridors, especially I-70, are a common route-planning factor."],
  ["MO", "MoDOT", "Missouri uses the shared 8-state baseline, with no separate state cert. Flood-prone river crossings can close routes seasonally."],
  ["NE", "Nebraska DOT", "Nebraska uses the shared 8-state baseline, with no separate state cert. Plains wind conditions and harvest-season traffic are common factors."],
  ["NV", "Nevada DOT", "Nevada uses the shared 8-state baseline, with no separate state cert. Extreme desert heat and long distances between services are key route-planning factors."],
  ["UT", "UDOT", "Utah uses the shared 8-state baseline, with no separate state cert. Mountain canyon routes may require extra clearance verification."],

  // Remaining states — frameworks vary; run on the group/federal baseline
  ["AK", "Alaska DOT&PF", "Alaska's vast distances and limited road network mean some regions are accessible only by barge or air — factor this into any route plan."],
  ["IL", "IDOT", "Illinois — Chicago metro routing complexity and congestion are a common planning factor."],
  ["IN", "INDOT", "Indiana sits at a major Midwest interstate crossroads with heavy through-traffic corridors."],
  ["MD", "MDOT SHA", "Maryland — DC-adjacent traffic and Chesapeake Bay Bridge clearance are common planning factors."],
  ["MA", "MassDOT", "Massachusetts has historic urban routes and tunnels with tight clearances — verify bridge and tunnel heights carefully."],
  ["ME", "MaineDOT", "Maine's rural and coastal routes include older, narrower bridges — verify clearances before a move."],
  ["MN", "MnDOT", "Minnesota — severe winter weather and lake-region routing are common planning factors."],
  ["MT", "MDT", "Montana's vast rural distances and wildlife-crossing zones are common route-planning factors."],
  ["ND", "NDDOT", "North Dakota — severe winter weather and agricultural harvest traffic are common planning factors."],
  ["OH", "Ohio DOT", "Ohio is a major Midwest industrial corridor with heavy interstate truck traffic."],
  ["OK", "Oklahoma DOT", "Oklahoma — tornado season may require monitoring severe weather alerts before and during a move."],
  ["PA", "PennDOT", "Pennsylvania's mountainous terrain and turnpike tunnels carry clearance restrictions — verify before routing through them."],
  ["RI", "RIDOT", "Rhode Island is the smallest state — most moves quickly cross into a neighboring state."],
  ["SD", "SDDOT", "South Dakota — long rural distances and severe winter weather are common planning factors."],
  ["VA", "VDOT", "Virginia — DC-adjacent traffic and mountainous western routes (I-81) are common planning factors."],
  ["VT", "VTrans", "Vermont's historic covered bridges and narrow rural roads may restrict height and width — verify before routing through them."],
  ["WV", "WVDOH", "West Virginia's mountainous terrain includes tunnels and steep grades — verify clearances before a move."],
  ["NJ", "NJDOT", "New Jersey has dense urban corridors and toll roads (Turnpike/Parkway) with their own restrictions."],
  ["NH", "NHDOT", "New Hampshire's rural roads and White Mountains routes carry seasonal weather considerations."],
];

SIMPLE_STATE_DATA.forEach(([code, agency, note]) => {
  STATE_OVERRIDES[code] = makeSimpleOverride(code, agency, note);
});

function getModuleForState(base, stateCode) {
  const ov = STATE_OVERRIDES[stateCode]?.modules?.[base.num];
  if (!ov) return base;
  return {
    ...base,
    blocks: [...base.blocks, ...(ov.blocks || [])],
    practice: [...base.practice, ...(ov.practice || [])],
  };
}

// ─── EQUIPMENT CHECKLIST (federal baseline, applies to any state) ──
const EQUIPMENT = [
  { icon: "☀️", name: "Amber Rotating/Strobe Light", spec: "360° visibility · Visible 500 ft minimum · Required when escorting", ref: "SAE J845 CLASS 1" },
  { icon: "🛑", name: "OVERSIZE LOAD Sign", spec: "18\" × 84\" minimum · Yellow background · Black retroreflective letters", ref: "MUTCD 6F.56" },
  { icon: "📻", name: "CB Radio", spec: "Channel 19 default · Mandatory two-way contact with carrier", ref: "FCC / DOT" },
  { icon: "📱", name: "Two-Way Radio", spec: "Recommended for lead & rear escort coordination", ref: "State DOT" },
  { icon: "🦺", name: "High-Vis Vest (ANSI 2+)", spec: "Class 2 in vehicle · Class 3 on roadway", ref: "OSHA 1910.132" },
  { icon: "🚩", name: "Red/Orange Flags", spec: "18\" × 18\" minimum · Load corners", ref: "MUTCD 6E.01" },
  { icon: "🧯", name: "Fire Extinguisher", spec: "2.5 lb ABC minimum · Securely mounted · Accessible", ref: "NFPA 10" },
  { icon: "➕", name: "First Aid Kit", spec: "OSHA Class A minimum · Fully stocked · Within expiration", ref: "OSHA 1910.151" },
  { icon: "✋", name: "STOP/SLOW Paddle", spec: "18\" diameter minimum · MUTCD compliant · Flagging operations", ref: "MUTCD 6E.03" },
  { icon: "🔺", name: "Traffic Control Devices", spec: "Cones or triangles as required by permit conditions", ref: "DOT 393.95" },
  { icon: "📄", name: "Permit & Documents", spec: "Original permit · Route plan · Insurance cert · Operator card", ref: "State DOT" },
];

// ─── QUICK REFERENCE (federal baseline — state cards append below) ─
const QUICKREF_BASE = [
  { h: "Federal Dimensions", rows: [["Max width", "8'6\" (102 in)"], ["Max GVW", "80,000 lbs"], ["Single axle max", "20,000 lbs"], ["Tandem axle max", "34,000 lbs"], ["Max length (single)", "65 ft"]] },
  { h: "Equipment Specifications", rows: [["OVERSIZE LOAD sign", "18\" × 84\" min"], ["Flag size", "18\" × 18\" min"], ["Amber light visibility", "500 ft"], ["STOP/SLOW paddle", "18\" diameter min"], ["Fire extinguisher", "2.5 lb ABC"], ["Flagger visibility", "500 ft"]] },
  { h: "Escort Positioning", rows: [["Lead escort gap (open hwy)", "500 ft minimum"], ["Rear escort gap", "100–300 ft"]] },
  { h: "Weather Restrictions", rows: [["Wind speed limit", "~35 mph sustained"], ["Sail-type load wind limit", "~25 mph"], ["Minimum visibility", "500 ft"]] },
  { h: "CB Channels", rows: [["Primary channel", "Channel 19"], ["Emergency only", "Channel 9"]] },
];

// ─── RESPONSIVE HOOK — real viewport-width detection, not CSS-only.
// Used to actually mount/unmount the sidebar on mobile instead of relying
// on display:none rules, which are easy to accidentally override.
function useIsMobile(breakpoint = 760) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// ─── MICRO COMPONENTS (all themed) ─────────────────────────────────

function SectionEyebrow({ text, T }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 16px" }}>
      <div style={{ flex: 1, height: 1, background: T.cardBorder }} />
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: T.subtext }}>{text}</span>
      <div style={{ flex: 1, height: 1, background: T.cardBorder }} />
    </div>
  );
}

function Pill({ text, color, light }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", padding: "2px 7px",
      borderRadius: 3, background: light, color, border: `1px solid ${color}33`, whiteSpace: "nowrap",
    }}>{text}</span>
  );
}

const BLOCK_STYLES = {
  key:  { bg: C.goldL,   border: "#C9A84C55", titleColor: "#8a6d1f", icon: "🔑" },
  warn: { bg: C.redL,    border: "#DC262633", titleColor: C.red,     icon: "⚠️" },
  tip:  { bg: C.blueL,   border: "#1D4ED833", titleColor: C.blue,    icon: "💡" },
  note: { bg: C.purpleL, border: "#7C3AED33", titleColor: C.purple,  icon: "📌" },
};
const BLOCK_STYLES_DARK = {
  key:  { bg: "rgba(201,168,76,.14)",  border: "#C9A84C55", titleColor: "#e8cd7a", icon: "🔑" },
  warn: { bg: "rgba(220,38,38,.14)",   border: "#DC262655", titleColor: "#f87171", icon: "⚠️" },
  tip:  { bg: "rgba(29,78,216,.16)",   border: "#1D4ED855", titleColor: "#7ea3ff", icon: "💡" },
  note: { bg: "rgba(124,58,237,.16)",  border: "#7C3AED55", titleColor: "#c9a6fb", icon: "📌" },
};

function ContentBlock({ block, T }) {
  if (block.type === "para") {
    return <p style={{ fontSize: 13, lineHeight: 1.75, color: T.text, margin: "12px 0" }}>{block.text}</p>;
  }
  const s = (T.dark ? BLOCK_STYLES_DARK : BLOCK_STYLES)[block.type];
  return (
    <div style={{ borderRadius: 9, padding: "13px 15px", margin: "14px 0", display: "flex", gap: 10, alignItems: "flex-start", border: `1px solid ${s.border}`, background: s.bg }}>
      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{s.icon}</span>
      <div>
        <span style={{ display: "block", fontWeight: 800, fontSize: 10.5, letterSpacing: "0.05em", textTransform: "uppercase", color: s.titleColor, marginBottom: 4 }}>{block.title}</span>
        <span style={{ fontSize: 12.5, lineHeight: 1.65, color: T.text }}>{block.text}</span>
      </div>
    </div>
  );
}

function ObjectivesBox({ objectives, T }) {
  return (
    <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderLeft: `4px solid ${C.gold}`, borderRadius: 8, padding: "14px 16px", margin: "14px 0" }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>Learning Objectives</div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {objectives.map((o, i) => (
          <li key={i} style={{ fontSize: 12.5, color: T.text, marginBottom: 5, paddingLeft: 18, position: "relative", lineHeight: 1.5 }}>
            <span style={{ position: "absolute", left: 0, color: C.green, fontWeight: 800 }}>✓</span>{o}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PracticeQuestion({ p, index, T, chosen, onAnswer }) {
  const answered = chosen !== undefined && chosen !== null;
  return (
    <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: "15px 16px", margin: "12px 0" }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.subtext, letterSpacing: "0.06em", marginBottom: 6 }}>PRACTICE QUESTION {index + 1}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.heading, marginBottom: 10, lineHeight: 1.5 }}>{p.q}</div>
      {p.options.map((opt, i) => {
        let bg = T.cardBg2, border = T.cardBorder, color = T.text, weight = 400;
        if (answered) {
          if (i === p.correct) { bg = C.greenL; border = C.green; color = "#065f46"; weight = 700; }
          else if (i === chosen) { bg = C.redL; border = C.red; color = "#991b1b"; weight = 700; }
        }
        return (
          <button key={i} disabled={answered} onClick={() => onAnswer(i)} style={{
            display: "block", width: "100%", textAlign: "left", padding: "8px 12px", marginBottom: 6,
            borderRadius: 6, border: `1px solid ${border}`, background: bg, color, fontWeight: weight,
            fontSize: 12.5, cursor: answered ? "default" : "pointer", fontFamily: "inherit",
          }}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize: 12, color: T.text, background: T.cardBg2, borderRadius: 6, padding: "10px 12px", marginTop: 8, lineHeight: 1.6 }}>
          <b>Explanation:</b> {p.explanation}
        </div>
      )}
    </div>
  );
}

// ─── LEAVE-CONFIRMATION MODAL ───────────────────────────────────────
function ConfirmModal({ T, title, message, confirmLabel, onYes, onNo }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: T.overlay, zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: T.cardBg, borderRadius: 16, padding: "24px 26px", maxWidth: 380, width: "100%", border: `1px solid ${T.cardBorder}`, boxShadow: T.shadow }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: T.heading, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: T.subtext, lineHeight: 1.6, marginBottom: 20 }}>{message}</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onNo} style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, fontWeight: 700, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>Stay Here</button>
          <button onClick={onYes} style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: C.red, color: "#fff", fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>{confirmLabel || "Yes, Leave"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── REDESIGNED STATE CARD (hover-enlarge + Enroll Now) ─────────────
function StateCard({ s, group, T, onEnroll }) {
  const [hover, setHover] = useState(false);
  const flag = STATE_FLAGS[s.code] || "🏳️";
  const ov = STATE_OVERRIDES[s.code];
  const agency = ov?.agency || s.cert || "State DOT";
  const detailed = DETAILED_STATES.has(s.code);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onEnroll(s.code)}
      style={{
        position: "relative", cursor: "pointer", background: T.cardBg,
        border: `1.5px solid ${hover ? group.color : T.cardBorder}`, borderRadius: 16, padding: 18,
        display: "flex", flexDirection: "column", minHeight: 190,
        transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
        transform: hover ? "translateY(-4px) scale(1.045)" : "none",
        boxShadow: hover ? `0 16px 34px ${group.color}40` : T.shadow,
        zIndex: hover ? 5 : 1,
      }}
    >
      {s.alert && <span style={{ position: "absolute", top: 14, right: 14, fontSize: 14 }}>⚠️</span>}
      <div style={{
        width: 50, height: 50, borderRadius: 14, marginBottom: 12, fontSize: 24,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${group.color}30, ${group.color}12)`,
        border: `1px solid ${group.color}40`,
      }}>{flag}</div>

      <div style={{ fontSize: 15, fontWeight: 900, color: T.heading, marginBottom: 2, letterSpacing: "-0.01em" }}>{s.name}</div>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: group.color, marginBottom: 8 }}>{agency}</div>

      <div style={{ fontSize: 11.5, color: T.subtext, lineHeight: 1.6, flex: 1 }}>
        Master {s.name}'s regulations and pass your {detailed ? "state-specific" : "PEVO Cert"} exam. Covers {agency} standards, escort protocols, and the full 14-module curriculum.
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${T.cardBorder}`, paddingTop: 12, marginTop: 12 }}>
        <span style={{ fontSize: 10, color: T.subtext, fontWeight: 700 }}>14 Modules + Exam Simulator</span>
        {!hover && <span style={{ fontSize: 12, fontWeight: 800, color: group.color }}>Start →</span>}
      </div>

      {hover && (
        <button
          onClick={(e) => { e.stopPropagation(); onEnroll(s.code); }}
          style={{
            position: "absolute", left: 14, right: 14, bottom: 14, padding: "11px 0", borderRadius: 10,
            border: "none", background: `linear-gradient(135deg, ${group.color}, ${group.color}cc)`,
            color: "#fff", fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit",
            boxShadow: `0 8px 20px ${group.color}55`,
          }}
        >
          Enroll Now →
        </button>
      )}
    </div>
  );
}

// ─── CLICKABLE GROUP SUMMARY TILE (scrolls to that group's section) ─
function GroupSummaryTile({ g, T, onJump }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onJump}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        borderRadius: 12, overflow: "hidden", border: `1px solid ${hover ? g.color : T.cardBorder}`,
        cursor: "pointer", transition: "transform .15s, box-shadow .15s",
        transform: hover ? "translateY(-3px)" : "none",
        boxShadow: hover ? `0 10px 24px ${g.color}35` : "none",
      }}
    >
      <div style={{ background: g.color, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, color: "#fff", fontWeight: 800, fontSize: 12 }}>
        <span>{g.icon} {g.label}</span>
        <span style={{ fontSize: 10, opacity: 0.85 }}>{hover ? "↓ View" : ""}</span>
      </div>
      <div style={{ background: T.dark ? `${g.color}22` : g.colorL, padding: "10px 14px" }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: g.color, lineHeight: 1 }}>{g.states.length}</div>
        <div style={{ fontSize: 10, color: T.subtext, marginTop: 3 }}>{g.sublabel}</div>
      </div>
    </div>
  );
}

// Flat index of every state (with its group attached) for instant search
const ALL_STATES_FLAT = Object.values(GROUPS).flatMap((g) => g.states.map((s) => ({ ...s, group: g })));

function StateSearchBox({ T, onEnroll }) {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);

  const matches = query.trim().length > 0
    ? ALL_STATES_FLAT.filter((s) =>
        s.code.toLowerCase().startsWith(query.trim().toLowerCase()) ||
        s.name.toLowerCase().includes(query.trim().toLowerCase())
      ).slice(0, 8)
    : [];

  const pick = (s) => { setResult(s); setQuery(s.name); setOpen(false); };
  const clear = () => { setResult(null); setQuery(""); };

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ position: "relative", maxWidth: 440 }}>
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (!e.target.value) setResult(null); }}
          onFocus={() => setOpen(true)}
          placeholder="🔍 Find your state — type a name or 2-letter code..."
          style={{
            width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${T.cardBorder}`,
            background: T.cardBg, color: T.text, fontSize: 12.5, fontFamily: "inherit", outline: "none",
          }}
        />
        {open && matches.length > 0 && (
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: T.cardBg,
            border: `1px solid ${T.cardBorder}`, borderRadius: 10, overflow: "hidden", zIndex: 20,
            boxShadow: T.shadow, maxHeight: 260, overflowY: "auto",
          }}>
            {matches.map((s) => (
              <button key={s.code} onClick={() => pick(s)} style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 12px",
                background: "transparent", border: "none", borderBottom: `1px solid ${T.cardBorder}`,
                cursor: "pointer", textAlign: "left", fontFamily: "inherit",
              }}>
                <span style={{ fontSize: 17 }}>{STATE_FLAGS[s.code] || "🏳️"}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.heading, flex: 1 }}>{s.name}</span>
                <Pill text={s.group.label} color={s.group.color} light={s.group.colorL} />
              </button>
            ))}
          </div>
        )}
      </div>

      {result && (
        <div style={{
          marginTop: 14, borderRadius: 14, padding: "18px 20px", maxWidth: 520,
          background: T.dark ? `${result.group.color}22` : result.group.colorL,
          border: `1.5px solid ${result.group.color}`, position: "relative",
        }}>
          <button onClick={clear} style={{
            position: "absolute", top: 12, right: 12, background: "transparent", border: "none",
            color: T.subtext, cursor: "pointer", fontSize: 14, fontFamily: "inherit",
          }}>✕</button>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <span style={{ fontSize: 30 }}>{STATE_FLAGS[result.code] || "🏳️"}</span>
            <div>
              <div style={{ fontSize: 17, fontWeight: 900, color: T.heading }}>{result.name}</div>
              <Pill text={`Belongs to: ${result.group.label}`} color={result.group.color} light={T.cardBg} />
            </div>
          </div>
          {result.width && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
              {[["W", result.width], ["H", result.height], ["Ins.", result.ins], ["Cert", result.cert]].map(([l, v]) => (
                <div key={l} style={{ background: T.cardBg, borderRadius: 8, padding: "6px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 8.5, color: T.subtext, textTransform: "uppercase" }}>{l}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: T.heading }}>{v}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11.5, color: T.text, lineHeight: 1.6, marginBottom: 14 }}>
            <b style={{ color: result.group.color }}>KEY RULE:</b> {result.group.keyRule}
          </div>
          <button onClick={() => onEnroll(result.code)} style={{
            padding: "10px 18px", borderRadius: 10, border: "none", background: result.group.color, color: "#fff",
            fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit",
          }}>Enroll Now — {result.name} →</button>
        </div>
      )}
    </div>
  );
}

// ─── ENROLL VIEW — group map + state picker ────────────────────────
function EnrollMap({ T, onStart }) {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div>
      <SectionEyebrow text="CHOOSE A STATE — 4-GROUP FRAMEWORK" T={T} />
      <StateSearchBox T={T} onEnroll={onStart} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 20 }}>
        {Object.values(GROUPS).map((g) => (
          <GroupSummaryTile key={g.id} g={g} T={T} onJump={() => scrollTo(`group-${g.id}`)} />
        ))}
      </div>

      {Object.values(GROUPS).map((g) => (
        <div key={g.id} id={`group-${g.id}`} style={{ marginBottom: 30, scrollMarginTop: 16 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 8, background: g.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{g.icon}</div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: T.heading }}>{g.label}</span>
                <Pill text={g.sublabel} color={g.color} light={g.colorL} />
                <Pill text={`${g.states.length} states`} color={C.muted} light={C.slate} />
              </div>
              <div style={{ fontSize: 11.5, color: T.subtext, marginTop: 4, maxWidth: 640, lineHeight: 1.6 }}>{g.description}</div>
            </div>
          </div>
          <div style={{
            padding: "8px 12px", borderRadius: 6, background: `${g.color}15`, border: `1px solid ${g.color}33`,
            fontSize: 10.5, color: T.text, marginBottom: 14,
          }}>
            <b style={{ color: g.color }}>KEY RULE:</b> {g.keyRule}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 14, paddingTop: 6 }}>
            {g.states.map((s) => <StateCard key={s.code} s={s} group={g} T={T} onEnroll={onStart} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── SIDEBAR (Quick Ref / Checklist / Modules / Progress %) ─────────
function Sidebar({ T, groupColor, progressPct, subTab, setSubTab, modNum, setModNum, modulePassed, stage, setStage, checklistComplete }) {
  const [modulesOpen, setModulesOpen] = useState(true);

  const navBtn = (active, onClick, label, extra, key) => (
    <button key={key} onClick={onClick} style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
      padding: "8px 10px", borderRadius: 8, border: "none", marginBottom: 4, cursor: "pointer", fontFamily: "inherit",
      background: active ? `${groupColor}18` : "transparent", color: active ? groupColor : T.subtext,
      fontWeight: active ? 800 : 600, fontSize: 12, textAlign: "left",
    }}>
      <span>{label}</span>{extra}
    </button>
  );

  return (
    <div className="ppca-sidebar" style={{
      width: 220, flexShrink: 0, background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 14,
      padding: 16, alignSelf: "flex-start", position: "sticky", top: 16,
    }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: T.subtext, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Course Progress</div>
        <div style={{ fontSize: 24, fontWeight: 900, color: groupColor, lineHeight: 1 }}>{progressPct}%</div>
        <div style={{ height: 6, borderRadius: 99, background: T.cardBg2, overflow: "hidden", marginTop: 8 }}>
          <div style={{ height: "100%", width: `${progressPct}%`, background: groupColor, transition: "width .3s" }} />
        </div>
      </div>

      {navBtn(stage === "study" && subTab === "quickref", () => { setStage("study"); setSubTab("quickref"); }, "📊 Quick Reference")}
      {navBtn(
        stage === "study" && subTab === "checklist",
        () => { setStage("study"); setSubTab("checklist"); },
        "✅ Checklist",
        checklistComplete ? <span style={{ fontSize: 11 }}>✅</span> : null
      )}

      <button
        onClick={() => setModulesOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
          background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit",
          margin: "14px 0 6px", padding: 0,
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 800, color: T.subtext, textTransform: "uppercase", letterSpacing: ".06em" }}>
          Modules ({BASE_MODULES.length})
        </span>
        <span style={{ fontSize: 11, color: T.subtext, transform: modulesOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</span>
      </button>

      {modulesOpen && (
        <div style={{ maxHeight: 380, overflowY: "auto", paddingRight: 2 }}>
          {BASE_MODULES.map((m) => {
            const passed = modulePassed(m.num);
            const active = stage === "study" && subTab === "modules" && modNum === m.num;
            return navBtn(
              active,
              () => { setStage("study"); setSubTab("modules"); setModNum(m.num); },
              `${m.icon} Module ${m.num}`,
              <span style={{ fontSize: 11 }}>{passed ? "✅" : ""}</span>,
              m.num
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── FINAL EXAM READINESS PAGE ──────────────────────────────────────
function ReadyForExamPage({ T, name, groupColor, questionCount, onStart }) {
  return (
    <div style={{ textAlign: "center", padding: "10px 6px 30px" }}>
      <div style={{ fontSize: 46, marginBottom: 6 }}>🎉</div>
      <h2 style={{ fontSize: 20, fontWeight: 900, color: T.heading, margin: "0 0 8px" }}>
        You've passed all 14 modules for {name}!
      </h2>
      <p style={{ fontSize: 13, color: T.subtext, lineHeight: 1.7, maxWidth: 520, margin: "0 auto 22px" }}>
        This crash course was built to make you genuinely knowledgeable and safe on the road as a pilot/escort
        vehicle operator — not just to pass a test. The PPCA Final Exam pulls questions from everything you just
        studied, so you'll know exactly what to expect walking into {name}'s official state or authority exam.
      </p>
      <div style={{ background: T.cardBg2, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: "16px 20px", maxWidth: 460, margin: "0 auto 24px", textAlign: "left" }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: groupColor, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>What the Final Exam Covers</div>
        {[
          `${questionCount} questions pulled from all 14 modules`,
          "Federal FHWA/MUTCD baseline + this state's specific rules",
          "70% required to pass and become PPCA Verified",
          "Missed questions are shown afterward with the correct answer",
          "You can retake immediately, or save your progress and come back",
        ].map((line, i) => (
          <div key={i} style={{ fontSize: 12, color: T.text, display: "flex", gap: 8, marginBottom: 6 }}>
            <span style={{ color: groupColor }}>✓</span>{line}
          </div>
        ))}
      </div>
      <button onClick={onStart} style={{
        padding: "13px 28px", borderRadius: 10, border: "none", background: groupColor, color: "#fff",
        fontWeight: 800, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit", boxShadow: `0 10px 24px ${groupColor}45`,
      }}>
        Start Final Exam →
      </button>
    </div>
  );
}

// ─── FINAL EXAM (all questions answered before grading) ─────────────
function FinalExam({ T, name, groupColor, questions, onSubmit }) {
  const [answers, setAnswers] = useState({});
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === questions.length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ fontSize: 17, fontWeight: 800, color: T.heading, margin: 0 }}>{name} — PPCA Final Exam</h2>
        <Pill text={`${answeredCount} / ${questions.length} answered`} color={groupColor} light={`${groupColor}18`} />
      </div>

      {questions.map((q, i) => (
        <div key={i} style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.subtext, marginBottom: 6 }}>QUESTION {i + 1} · MODULE {q.moduleNum}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.heading, marginBottom: 10, lineHeight: 1.5 }}>{q.q}</div>
          {q.options.map((opt, oi) => (
            <button key={oi} onClick={() => setAnswers((a) => ({ ...a, [i]: oi }))} style={{
              display: "block", width: "100%", textAlign: "left", padding: "8px 12px", marginBottom: 6, borderRadius: 6,
              border: `1px solid ${answers[i] === oi ? groupColor : T.cardBorder}`,
              background: answers[i] === oi ? `${groupColor}18` : T.cardBg2,
              color: answers[i] === oi ? groupColor : T.text, fontWeight: answers[i] === oi ? 700 : 400,
              fontSize: 12.5, cursor: "pointer", fontFamily: "inherit",
            }}>
              {String.fromCharCode(65 + oi)}. {opt}
            </button>
          ))}
        </div>
      ))}

      <button
        disabled={!allAnswered}
        onClick={() => onSubmit(answers)}
        style={{
          width: "100%", padding: "13px 0", borderRadius: 10, border: "none", marginTop: 8,
          background: allAnswered ? groupColor : T.cardBg2, color: allAnswered ? "#fff" : T.subtext,
          fontWeight: 800, fontSize: 13.5, cursor: allAnswered ? "pointer" : "default", fontFamily: "inherit",
        }}
      >
        {allAnswered ? "Submit Exam →" : `Answer all questions to submit (${answeredCount}/${questions.length})`}
      </button>
    </div>
  );
}

// ─── EXAM RESULTS ────────────────────────────────────────────────────
function ExamResults({ T, name, groupColor, questions, answers, score, passed, onRetakeNow, onSubmitLater, onContinueCertificate, onReviewModule }) {
  const missed = questions.map((q, i) => ({ ...q, chosen: answers[i], idx: i })).filter((q) => q.chosen !== q.correct);

  return (
    <div>
      <div style={{ textAlign: "center", padding: "10px 0 20px" }}>
        <div style={{ fontSize: 40 }}>{passed ? "✅" : "📋"}</div>
        <h2 style={{ fontSize: 20, fontWeight: 900, color: passed ? C.green : C.red, margin: "8px 0 4px" }}>
          {passed ? "You passed!" : "Not quite — let's review"}
        </h2>
        <div style={{ fontSize: 13, color: T.subtext }}>You scored <b style={{ color: T.heading }}>{score}%</b> on the {name} PPCA Final Exam ({questions.length - missed.length} of {questions.length} correct).</div>
      </div>

      {!passed && (
        <div style={{ background: C.redL, border: `1px solid ${C.red}33`, borderRadius: 10, padding: "14px 16px", marginBottom: 16, color: "#7f1d1d", fontSize: 12.5, lineHeight: 1.7 }}>
          You need 70% to pass. Take a look at what you missed below — every professional escort operator retakes this exam at least once. Review the flagged modules, then come back whenever you're ready.
        </div>
      )}

      {missed.length > 0 && (
        <>
          <SectionEyebrow text="QUESTIONS YOU MISSED" T={T} />
          {missed.map((q) => (
            <div key={q.idx} style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.subtext, marginBottom: 6 }}>QUESTION {q.idx + 1} · MODULE {q.moduleNum}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.heading, marginBottom: 8 }}>{q.q}</div>
              <div style={{ fontSize: 12, color: "#991b1b", marginBottom: 4 }}>Your answer: {q.options[q.chosen] ?? "— not answered —"}</div>
              <div style={{ fontSize: 12, color: "#065f46", marginBottom: 8 }}>Correct answer: {q.options[q.correct]}</div>
              <div style={{ fontSize: 12, color: T.text, background: T.cardBg2, borderRadius: 6, padding: "8px 10px", lineHeight: 1.6, marginBottom: 8 }}>
                <b>Explanation:</b> {q.explanation}
              </div>
              <button onClick={() => onReviewModule(q.moduleNum)} style={{
                fontSize: 11.5, fontWeight: 700, color: groupColor, background: "transparent",
                border: `1px solid ${groupColor}55`, borderRadius: 99, padding: "5px 12px", cursor: "pointer", fontFamily: "inherit",
              }}>
                Review Module {q.moduleNum} →
              </button>
            </div>
          ))}
        </>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
        {passed ? (
          <button onClick={onContinueCertificate} style={{
            flex: 1, minWidth: 200, padding: "13px 0", borderRadius: 10, border: "none", background: C.green,
            color: "#fff", fontWeight: 800, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit",
          }}>🏅 Continue to Certificate →</button>
        ) : (
          <>
            <button onClick={onRetakeNow} style={{
              flex: 1, minWidth: 180, padding: "13px 0", borderRadius: 10, border: "none", background: groupColor,
              color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
            }}>🔄 Retake Exam Now</button>
            <button onClick={onSubmitLater} style={{
              flex: 1, minWidth: 180, padding: "13px 0", borderRadius: 10, border: `1px solid ${T.cardBorder}`,
              background: "transparent", color: T.text, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
            }}>Save & Retake Later</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── CERTIFICATE / VERIFIED BADGE PAGE ──────────────────────────────
function CertificatePage({ T, code, name, groupColor, score, onDone }) {
  const certId = `PPCA-${code}-${Date.now().toString(36).toUpperCase()}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(certId + " | PilotLinkUSA PPCA Verified | " + name)}`;
  const dateStr = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const download = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>PPCA Certificate</title>
      <style>body{font-family:Arial,sans-serif;background:#0d1f3c;color:#fff;padding:60px;text-align:center;}
      .card{border:3px solid #C9A84C;border-radius:16px;padding:50px;max-width:640px;margin:0 auto;}
      h1{color:#C9A84C;letter-spacing:2px;}</style></head><body>
      <div class="card"><h1>PPCA VERIFIED</h1><h2>${name}</h2>
      <p>This certifies that the holder has completed the PilotLinkUSA PPCA Pre-Exam Crash Course for ${name}
      and passed the Final Exam with a score of ${score}%.</p>
      <p>Certificate ID: ${certId}</p><p>Issued: ${dateStr}</p></div></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `PPCA-Certificate-${code}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ textAlign: "center", padding: "10px 0 10px" }}>
      <div style={{ fontSize: 52 }}>🏅</div>
      <h2 style={{ fontSize: 21, fontWeight: 900, color: T.heading, margin: "8px 0 4px" }}>Congratulations — you're PPCA Verified!</h2>
      <div style={{ fontSize: 13, color: T.subtext, marginBottom: 22 }}>
        You scored <b style={{ color: groupColor }}>{score}%</b> and passed the {name} PPCA Final Exam.
        This verified badge is now on your PilotLinkUSA dashboard.
      </div>

      <div style={{
        maxWidth: 420, margin: "0 auto 22px", background: T.heroGrad, borderRadius: 16,
        border: `2px solid ${C.gold}`, padding: "24px 26px", color: "#fff",
      }}>
        <div style={{ fontSize: 10, letterSpacing: ".18em", color: C.gold, fontWeight: 800, marginBottom: 6 }}>PPCA VERIFIED OPERATOR</div>
        <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 4 }}>{name}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,.6)", marginBottom: 16 }}>Issued {dateStr} · Score {score}%</div>
        <img src={qrUrl} alt="Verification QR code" style={{ borderRadius: 8, background: "#fff", padding: 6 }} />
        <div style={{ fontSize: 9.5, color: "rgba(255,255,255,.55)", marginTop: 10, letterSpacing: ".04em" }}>{certId}</div>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,.4)", marginTop: 6 }}>Scan to verify this operator passed the {name} PPCA course</div>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
        <button onClick={download} style={{ padding: "11px 20px", borderRadius: 10, border: "none", background: C.gold, color: C.navy, fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>⬇️ Download Certificate</button>
        <button onClick={onDone} style={{ padding: "11px 20px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, fontWeight: 700, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>Go to Dashboard →</button>
      </div>
    </div>
  );
}

// ─── COURSE VIEW — 14-module course + exam flow for one state ──────
function CourseView({ stateCode, T, onRequestExit, onRecordAttempt }) {
  const isMobile = useIsMobile();
  const meta = findStateMeta(stateCode);
  const override = STATE_OVERRIDES[stateCode];
  const name = override?.name || meta?.name || stateCode;
  const flag = override?.flag || STATE_FLAGS[stateCode] || "🏳️";
  const agency = override?.agency || "State DOT";
  const m = override?.meta || meta || {};
  const groupColor = meta?.groupColor || C.navy;

  const [subTab, setSubTab] = useState("modules"); // modules | checklist | quickref
  const [modNum, setModNum] = useState(1);
  const [answers, setAnswers] = useState({}); // `${modNum}-${qIndex}` -> chosen index
  const [stage, setStage] = useState("study"); // study | ready | exam | results | certificate
  const [examResult, setExamResult] = useState(null);
  const [checkedItems, setCheckedItems] = useState({}); // equipment checklist
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile-only sidebar toggle

  // Scroll to the top of the page whenever the user moves to a new module,
  // sub-tab, or stage — so they don't land mid-scroll on a fresh screen.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [modNum, subTab, stage]);

  const checklistComplete = EQUIPMENT.every((_, i) => checkedItems[i]);

  const moduleContent = (num) => getModuleForState(BASE_MODULES.find((x) => x.num === num), stateCode);
  const activeModule = moduleContent(modNum);
  const quickref = [...QUICKREF_BASE, ...(override?.quickref || [])];

  const modulePassed = (num) => {
    const mod = moduleContent(num);
    if (!mod.practice.length) return true;
    return mod.practice.every((p, i) => answers[`${num}-${i}`] === p.correct);
  };
  const passedCount = BASE_MODULES.filter((mm) => modulePassed(mm.num)).length;
  const progressPct = Math.round((passedCount / BASE_MODULES.length) * 100);
  const allModulesPassed = passedCount === BASE_MODULES.length;

  const answerQuestion = (num, qIdx, choiceIdx) => {
    setAnswers((a) => ({ ...a, [`${num}-${qIdx}`]: choiceIdx }));
  };
  const retakeModule = (num) => {
    setAnswers((a) => {
      const next = { ...a };
      Object.keys(next).forEach((k) => { if (k.startsWith(`${num}-`)) delete next[k]; });
      return next;
    });
  };

  // Build the final exam question bank from every module for this state
  const examQuestions = BASE_MODULES.flatMap((bm) => {
    const mod = moduleContent(bm.num);
    return mod.practice.map((p) => ({ ...p, moduleNum: bm.num }));
  });

  const submitExam = (rawAnswers) => {
    const correct = examQuestions.filter((q, i) => rawAnswers[i] === q.correct).length;
    const score = Math.round((correct / examQuestions.length) * 100);
    const passed = score >= 70;
    setExamResult({ answers: rawAnswers, score, passed });
    onRecordAttempt(stateCode, name, score, passed);
    setStage("results");
  };

  const StageHeader = (
    <div style={{
      background: T.heroGrad, borderRadius: 14, padding: "20px 24px",
      display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, marginBottom: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <button onClick={onRequestExit} title="Back to all states" style={{
          background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.15)", color: "#fff",
          borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 12, fontFamily: "inherit",
        }}>←</button>
        <div style={{ width: 56, height: 56, borderRadius: 12, background: C.gold, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>{flag}</div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.gold, letterSpacing: "0.1em" }}>PPCA CRASH COURSE</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" }}>{name} <span style={{ color: "rgba(255,255,255,.5)", fontWeight: 600, fontSize: 13 }}>· {agency}</span></div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[["W", m.width || "—"], ["H", m.height || "—"], ["Ins.", m.ins || "Verify w/ state"], ["Cert", m.cert || "Verify w/ state"]].map(([l, v]) => (
          <div key={l} style={{ padding: "6px 10px", borderRadius: 6, background: "rgba(255,255,255,.06)", border: `1px solid ${C.gold}33`, textAlign: "center", minWidth: 70 }}>
            <div style={{ fontSize: 8.5, color: "rgba(255,255,255,.5)", textTransform: "uppercase" }}>{l}</div>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.gold }}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      {StageHeader}

      {!DETAILED_STATES.has(stateCode) && stage === "study" && (
        <div style={{ padding: "10px 14px", borderRadius: 7, background: T.cardBg2, border: `1px solid ${T.cardBorder}`, fontSize: 11.5, color: T.subtext, marginBottom: 16, lineHeight: 1.6 }}>
          ℹ️ {name} is running on the shared baseline course rather than a fully hand-built state course yet. Always verify current requirements with {name}'s DOT before a move.
        </div>
      )}

      {stage === "study" && (
        <div>
          {isMobile && (
            <button
              onClick={() => setSidebarOpen((o) => !o)}
              style={{
                display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", gap: 8,
                padding: "10px 14px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: T.cardBg,
                color: T.heading, fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", marginBottom: 12,
              }}
            >
              <span>📋 Course Menu ({progressPct}% complete)</span>
              <span style={{ transform: sidebarOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</span>
            </button>
          )}

          <div className="ppca-course-layout" style={{ display: "flex", gap: 18, alignItems: "flex-start", flexDirection: isMobile ? "column" : "row" }}>
            {(!isMobile || sidebarOpen) && (
              <Sidebar
                T={T} groupColor={groupColor} progressPct={progressPct}
                subTab={subTab} setSubTab={setSubTab} modNum={modNum} setModNum={setModNum}
                modulePassed={modulePassed} stage={stage} setStage={setStage}
                checklistComplete={checklistComplete}
              />
            )}

            <div style={{ flex: 1, minWidth: 0, width: "100%" }}>
              {subTab === "modules" && (
              <div>
                <div style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "0 0 8px" }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: C.navy, color: C.gold, fontWeight: 900, fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {activeModule.icon}
                  </div>
                  <div>
                    <h2 style={{ fontSize: 18, fontWeight: 800, color: T.heading, letterSpacing: "-0.02em", margin: "0 0 4px" }}>
                      Module {activeModule.num} — {activeModule.title}
                    </h2>
                    <div style={{ fontSize: 11, color: T.subtext, fontWeight: 600 }}>Estimated study time · {activeModule.time}</div>
                  </div>
                </div>

                <ObjectivesBox objectives={activeModule.objectives} T={T} />
                {activeModule.blocks.map((b, i) => <ContentBlock key={i} block={b} T={T} />)}

                <SectionEyebrow text="PRACTICE QUESTIONS — ANSWER ALL CORRECTLY TO CONTINUE" T={T} />
                {activeModule.practice.map((p, i) => (
                  <PracticeQuestion
                    key={i} p={p} index={i} T={T}
                    chosen={answers[`${modNum}-${i}`]}
                    onAnswer={(choice) => answerQuestion(modNum, i, choice)}
                  />
                ))}

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, gap: 10, flexWrap: "wrap" }}>
                  <button
                    disabled={modNum === 1}
                    onClick={() => setModNum((n) => n - 1)}
                    style={{
                      padding: "9px 16px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: "transparent",
                      color: modNum === 1 ? T.cardBorder : T.text, fontWeight: 700, fontSize: 12.5,
                      cursor: modNum === 1 ? "default" : "pointer", fontFamily: "inherit",
                    }}
                  >← Previous Module</button>

                  {!modulePassed(modNum) ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 11.5, color: T.subtext }}>Answer every question correctly to unlock the next module.</span>
                      {Object.keys(answers).some((k) => k.startsWith(`${modNum}-`)) && (
                        <button onClick={() => retakeModule(modNum)} style={{
                          padding: "9px 16px", borderRadius: 8, border: "none", background: C.red, color: "#fff",
                          fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                        }}>🔄 Retake This Module</button>
                      )}
                    </div>
                  ) : modNum < 14 ? (
                    <button onClick={() => setModNum((n) => n + 1)} style={{
                      padding: "9px 16px", borderRadius: 8, border: "none", background: groupColor, color: "#fff",
                      fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit",
                    }}>Next Module →</button>
                  ) : (
                    <button onClick={() => setStage("ready")} style={{
                      padding: "9px 16px", borderRadius: 8, border: "none", background: C.gold, color: C.navy,
                      fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit",
                    }}>🎉 All Modules Passed — Continue →</button>
                  )}
                </div>
              </div>
            )}

            {subTab === "checklist" && (
              <div>
                <SectionEyebrow text={`REQUIRED EQUIPMENT CHECKLIST — ${name.toUpperCase()}`} T={T} />
                {checklistComplete && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 9,
                    background: C.greenL, border: `1px solid ${C.green}44`, color: "#065f46", fontSize: 12.5,
                    fontWeight: 700, marginBottom: 14,
                  }}>
                    ✅ Checklist Complete — every required item has been verified for {name}.
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 8 }}>
                  {EQUIPMENT.map((e, i) => {
                    const checked = !!checkedItems[i];
                    return (
                      <div
                        key={i}
                        onClick={() => setCheckedItems((c) => ({ ...c, [i]: !c[i] }))}
                        style={{
                          display: "flex", gap: 10, padding: "10px 12px", borderRadius: 7, cursor: "pointer",
                          background: checked ? C.greenL : T.cardBg,
                          border: `1px solid ${checked ? C.green + "66" : T.cardBorder}`,
                          transition: "all .15s",
                        }}
                      >
                        <div style={{
                          width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                          background: checked ? C.green : "transparent",
                          border: `1.5px solid ${checked ? C.green : T.cardBorder}`,
                          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff",
                        }}>
                          {checked && "✓"}
                        </div>
                        <span style={{ fontSize: 15, flexShrink: 0 }}>{e.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: checked ? "#065f46" : T.heading, textDecoration: checked ? "line-through" : "none", opacity: checked ? 0.75 : 1 }}>{e.name}</div>
                          <div style={{ fontSize: 10, color: T.subtext, lineHeight: 1.5, marginTop: 1 }}>{e.spec}</div>
                        </div>
                        <span style={{ fontSize: 9, color: T.subtext, flexShrink: 0, background: T.cardBg2, padding: "2px 5px", borderRadius: 3, alignSelf: "flex-start" }}>{e.ref}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {subTab === "quickref" && (
              <div>
                <SectionEyebrow text={`QUICK REFERENCE — ${name.toUpperCase()}`} T={T} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
                  {quickref.map((card, i) => (
                    <div key={i} style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 10, overflow: "hidden" }}>
                      <div style={{ background: C.navy, color: C.gold, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", padding: "9px 14px" }}>{card.h.toUpperCase()}</div>
                      {card.rows.map(([k, v], j) => (
                        <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", borderBottom: j < card.rows.length - 1 ? `1px solid ${T.cardBorder}` : "none", fontSize: 12 }}>
                          <span style={{ color: T.subtext }}>{k}</span><span style={{ fontWeight: 800, color: T.heading }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      )}

      {stage === "ready" && (
        <ReadyForExamPage T={T} name={name} groupColor={groupColor} questionCount={examQuestions.length} onStart={() => setStage("exam")} />
      )}

      {stage === "exam" && (
        <FinalExam T={T} name={name} groupColor={groupColor} questions={examQuestions} onSubmit={submitExam} />
      )}

      {stage === "results" && examResult && (
        <ExamResults
          T={T} name={name} groupColor={groupColor} questions={examQuestions}
          answers={examResult.answers} score={examResult.score} passed={examResult.passed}
          onRetakeNow={() => setStage("exam")}
          onSubmitLater={onRequestExit}
          onContinueCertificate={() => setStage("certificate")}
          onReviewModule={(num) => { setStage("study"); setSubTab("modules"); setModNum(num); }}
        />
      )}

      {stage === "certificate" && examResult && (
        <CertificatePage T={T} code={stateCode} name={name} groupColor={groupColor} score={examResult.score} onDone={onRequestExit} />
      )}
    </div>
  );
}

// ─── DASHBOARD — attempt history + certificates ─────────────────────
function DashboardView({ T, records, onGoToState }) {
  const entries = Object.entries(records);
  if (!entries.length) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", color: T.subtext }}>
        <div style={{ fontSize: 34, marginBottom: 10 }}>🎓</div>
        <div style={{ fontSize: 14 }}>No courses started yet. Enroll in a state to begin building your dashboard.</div>
      </div>
    );
  }
  return (
    <div>
      <SectionEyebrow text="MY PPCA DASHBOARD" T={T} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 14 }}>
        {entries.map(([code, rec]) => {
          const meta = findStateMeta(code);
          const name = STATE_OVERRIDES[code]?.name || meta?.name || code;
          const flag = STATE_OVERRIDES[code]?.flag || STATE_FLAGS[code] || "🏳️";
          const last = rec.attempts[rec.attempts.length - 1];
          return (
            <div key={code} style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 24 }}>{flag}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: T.heading }}>{name}</div>
                  <div style={{ fontSize: 10.5, color: T.subtext }}>{rec.attempts.length} attempt{rec.attempts.length !== 1 ? "s" : ""}</div>
                </div>
                {rec.certified && <span style={{ marginLeft: "auto", fontSize: 20 }}>🏅</span>}
              </div>
              <div style={{ fontSize: 11.5, color: T.subtext, marginBottom: 12 }}>
                Last score: <b style={{ color: rec.certified ? C.green : T.heading }}>{last.score}%</b> {rec.certified ? "— PPCA Verified" : "— needs 70% to pass"}
              </div>
              <button onClick={() => onGoToState(code)} style={{
                width: "100%", padding: "9px 0", borderRadius: 8, border: "none",
                background: rec.certified ? C.gold : C.navy, color: rec.certified ? C.navy : "#fff",
                fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              }}>
                {rec.certified ? "View Certificate →" : "Continue / Retake →"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────
export default function PPCAAllStatesCrashCourse({ isDarkMode, toggleTheme }) {
  const [view, setView] = useState("enroll"); // enroll | course | dashboard
  const [selectedState, setSelectedState] = useState(null);
  const [records, setRecords] = useState({}); // code -> { attempts: [{score, passed, date}], certified }
  const [confirmModal, setConfirmModal] = useState(null);

  const [localDark, setLocalDark] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("theme") === "dark"
  );
  const D = isDarkMode !== undefined ? isDarkMode : localDark;
  const T = getTheme(D);
  const toggleDark = () => {
    const next = !localDark;
    setLocalDark(next);
    localStorage.setItem("theme", next ? "dark" : "light");
    document.documentElement.classList.toggle("dark", next);
    if (toggleTheme) toggleTheme();
  };

  const requestLeave = (navigateFn) => {
    if (view === "course") {
      setConfirmModal({ navigateFn });
    } else {
      navigateFn();
    }
  };

  const startState = (code) => { setSelectedState(code); setView("course"); };
  const exitCourse = () => requestLeave(() => { setView("enroll"); });
  const goDashboard = () => requestLeave(() => setView("dashboard"));

  const recordAttempt = (code, name, score, passed) => {
    setRecords((r) => {
      const prev = r[code] || { attempts: [], certified: false };
      return {
        ...r,
        [code]: {
          attempts: [...prev.attempts, { score, passed, date: new Date().toLocaleDateString() }],
          certified: prev.certified || passed,
        },
      };
    });
  };

  return (
    <div style={{ fontFamily: "'Inter','Helvetica Neue',Arial,sans-serif", background: T.pageBg, minHeight: "100vh", color: T.text, transition: "background .3s" }}>
      <style>{`
        @media (max-width: 760px) {
          .ppca-sidebar { width: 100% !important; position: static !important; }
        }
      `}</style>
      <Navbar isDarkMode={D} toggleTheme={toggleDark} />

      <div style={{ background: T.heroGrad, borderBottom: `1px solid ${C.gold}33`, padding: "26px 24px" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.gold, letterSpacing: "0.12em", marginBottom: 8 }}>PPCA PRE-EXAM CRASH COURSE</div>
            <h1 style={{ fontSize: 25, fontWeight: 900, color: "#fff", margin: "0 0 6px", letterSpacing: "-0.03em" }}>
              One 14-Module Curriculum. <span style={{ color: C.gold }}>All 50 States.</span>
            </h1>
            <p style={{ fontSize: 12.5, color: "rgba(255,255,255,.6)", margin: 0, maxWidth: 640, lineHeight: 1.6 }}>
              Every state runs the same federal MUTCD/FHWA base course — pick a state below and its specific rules, numbers, and exam quirks layer on top automatically.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {view === "course" && (
              <button onClick={exitCourse} style={{
                padding: "9px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,.2)", background: "rgba(255,255,255,.06)",
                color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              }}>← All States</button>
            )}
            <button onClick={goDashboard} style={{
              padding: "9px 16px", borderRadius: 8, border: "none", background: C.gold,
              color: C.navy, fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
            }}>🎓 My Dashboard</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "24px clamp(12px, 4vw, 24px) 48px" }}>
        {view === "enroll" && <EnrollMap T={T} onStart={startState} />}
        {view === "dashboard" && <DashboardView T={T} records={records} onGoToState={(code) => startState(code)} />}
        {view === "course" && selectedState && (
          <CourseView
            stateCode={selectedState} T={T}
            onRequestExit={exitCourse}
            onRecordAttempt={recordAttempt}
          />
        )}

        <div style={{ marginTop: 32, padding: "12px 16px", borderRadius: 7, background: T.dark ? "rgba(180,83,9,.16)" : C.amberL, border: `1px solid ${C.amber}33`, fontSize: 10, color: T.dark ? "#f2c481" : C.amber, lineHeight: 1.6 }}>
          ℹ️ <strong>Good to know:</strong> This PPCA training content is for educational pre-exam preparation only.
          It does not constitute state certification or authorization to operate as a pilot/escort vehicle operator.
          Always verify current requirements with the relevant state DOT before every permitted move. Regulations
          change — confirm before each operation. © {new Date().getFullYear()} PilotLinkUSA
        </div>
      </div>

      {confirmModal && (
        <ConfirmModal
          T={T}
          title="Leave this study session?"
          message="You're in the middle of this course. If you leave now, your answers for this session will be cleared. Are you sure you want to leave?"
          confirmLabel="Yes, Leave"
          onYes={() => { confirmModal.navigateFn(); setConfirmModal(null); }}
          onNo={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
}
